import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { freight } from "../lib/freightApi.js";
import Recruiting from "./Recruiting.jsx";
import LiveListen, { primeAudio } from "./LiveListen.jsx";
import { driversFromApi, loadFromApi, loadsFromApi, dispatchersFromApi, callsFromApi, buildPlan, planOverrides, exceptionsFromCalls, UNANSWERED_BEFORE_ESCALATING } from "./fromApi.js";

/* --------------------------------- tokens ---------------------------------- */

const T = {
  ink: "#000000", ink2: "#5A5A5A", ink3: "#8C8C8C", ink4: "#BFBFBF",
  hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5", surface: "#FFFFFF",
};
const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;
const MIN = 60000;
const AGO = (m) => Date.now() - m * MIN;

/* ------------------------------- staff, roles ------------------------------ */

// ⛔ THE FOUR INVENTED DISPATCHERS ARE DELETED. Nothing read this array any more — the live
// registry below is what every lookup uses — but "Hollis Grant" was still being read off it
// by hand into the escalation ladder, and a dead array of plausible people is exactly what
// the next person needing a name will reach for. Real dispatchers come from
// GET /api/carrier/dispatchers.


/* ------------------------------- drivers, ELD ------------------------------ */

const UPDATE_VIA = {
  call: "Phone call", macropoint: "MacroPoint tracking", truckertools: "Trucker Tools",
  email: "Email only", portal: "Their portal",
};

/* ---------------------------------- loads ---------------------------------- */

const L1_STOPS = [
  { seq: 1, type: "pickup", name: "Gulf Coast Terminal", city: "Houston, TX", window: "Today 08:00–11:00", windowEnd: "11:00 AM", state: "complete", arrivedAt: AGO(310) },
  { seq: 2, type: "drop", name: "Rio Grande DC", city: "Laredo, TX", window: "Today 20:00–23:00", windowEnd: "11:00 PM", state: "pending", etaAt: "8:40 PM" },
];
const L2_STOPS = [
  { seq: 1, type: "pickup", name: "Baytown Chemical", city: "Baytown, TX", window: "Today 13:00–15:00", windowEnd: "3:00 PM", state: "pending", etaAt: "1:40 PM" },
  { seq: 2, type: "pickup", name: "Channelview Annex", city: "Channelview, TX", window: "Today 15:30–16:30", windowEnd: "4:30 PM", state: "pending", etaAt: "5:10 PM" },
  { seq: 3, type: "drop", name: "Trinity Crossdock", city: "Dallas, TX", window: "Tomorrow 06:00–09:00", windowEnd: "9:00 AM", state: "pending", etaAt: "7:30 AM" },
];
const L3_STOPS = [
  { seq: 1, type: "pickup", name: "Pasadena Yard 4", city: "Pasadena, TX", window: "Today 06:00–08:00", windowEnd: "8:00 AM", state: "complete", arrivedAt: AGO(520) },
  { seq: 2, type: "drop", name: "Port Allen Depot", city: "New Orleans, LA", window: "Today 18:00–21:00", windowEnd: "9:00 PM", state: "arrived", arrivedAt: AGO(148) },
];
const L4_STOPS = [
  { seq: 1, type: "pickup", name: "Katy Freeway Cold", city: "Katy, TX", window: "Today 11:00–13:00", windowEnd: "1:00 PM", state: "complete", arrivedAt: AGO(200) },
  { seq: 2, type: "drop", name: "Bandera Foods", city: "San Antonio, TX", window: "Today 19:00–21:00", windowEnd: "9:00 PM", state: "pending", etaAt: "7:15 PM" },
];
const L5_STOPS = [
  { seq: 1, type: "pickup", name: "Deer Park Terminal", city: "Deer Park, TX", window: "Yesterday 14:00–16:00", windowEnd: "4:00 PM", state: "canceled" },
  { seq: 2, type: "drop", name: "Beaumont Yard", city: "Beaumont, TX", window: "Yesterday 19:00–21:00", windowEnd: "9:00 PM", state: "canceled" },
];

/* --------------------------------- handoffs -------------------------------- */

const KIND = {
  incident: "Incident — person now",
  money: "Money",
  appointment: "Appointment change",
  concern: "Driver concern",
  unreachable: "Needs a person",
};

/* money asks, so routing isn't guesswork */
const MONEY_KIND = {
  lumper: { label: "Lumper fee", to: "billing" },
  advance: { label: "Fuel advance", to: "billing" },
  layover: { label: "Layover", to: "ops" },
  detention: { label: "Detention", to: "billing" },
  scale: { label: "Scale ticket", to: "billing" },
  tarp: { label: "Tarp pay", to: "billing" },
  assist: { label: "Driver assist", to: "ops" },
};

const NEEDS = { incident: "safety", money: "billing", appointment: "ops", concern: "ops", unreachable: "ops" };

/**
 * ⛔ THE EXCEPTIONS QUEUE HAS NO DATA MODEL. THIS IS EMPTY ON PURPOSE.
 *
 * It used to hold six hand-written handoffs — a tire blowout on I-10 with a six-field intake
 * form, a $175 lumper fee, a detention clock at Port Allen. There is no table, no column and
 * no endpoint behind any of it: nothing turns a finished call into an exception. So it could
 * not be "wired", and leaving the illustration in place had two costs.
 *
 * The first is honesty: a carrier saw six urgent handoffs about drivers they had never
 * hired, on the first tab, next to their own empty roster.
 *
 * The second is that IT CRASHED THE BOARD. Each row was keyed to a driver id "d1".."d4";
 * real driver ids are Postgres SERIAL integers, so `drivers.find(...)` returned undefined
 * and `QueueItem` read `.name` off it. `/carrier/dispatch` was a white screen on first
 * paint, and `vite build` was green the whole time. Found by an independent review, and
 * reproduced in Dispatch.render.test.mjs before this change.
 *
 * Restoring this means designing the model first: what a dispatcher must be handed, which
 * call outcomes produce one, and who it escalates to.
 */

// ⛔ THE ESCALATION LADDER IS GONE, BECAUSE NOTHING CLIMBS IT.
//
// This was four timed rungs — primary, second contact at +20m, operations manager at +45m,
// emergency contact at +90m — drawn on every handoff, with the rung matching the wait time
// filled in as though it had been reached. The third rung's label was the string
// "Hollis Grant", a name lifted from the deleted demo staff array, printed beside the real
// driver's real name.
//
// The only outbound path in this app is placeDriverCall → core with `to: driver.phoneE164`
// (backend/src/calls.mjs). There is no code anywhere that dials a second contact, an ops
// manager or an emergency contact. A two-hour-old exception rendered "Emergency contact" as
// the current rung — the screen telling a dispatcher that the driver's family had been
// called, when nobody had called anybody.
//
// It comes back when something actually escalates. A ladder is a claim about what the
// system did, not a diagram of what it ought to do.

/**
 * ⛔ WELLBEING FLAGS HAVE NO DATA MODEL EITHER. Empty for the same two reasons: nothing
 * records what a driver said about being tired or needing to get home, and the hand-written
 * rows were keyed to the same non-existent string driver ids (they crashed the Drivers tab
 * at `WellbeingCard driver={...}` → `driver.name`).
 *
 * This one deserves particular care when it is built: it is a record of something personal
 * a driver said in passing, and the screen promises it "never touches a performance record".
 * That promise has to be true in the schema, not just in the copy.
 */

/**
 * ⛔ DRIVER ERRANDS HAVE NO DATA MODEL. Empty for the same reasons — and this was the throw
 * that actually killed the Drivers tab first, at `SEED_ERRANDS.map(...)` → `d.name`, because
 * it evaluated during that tab's own render.
 */

/**
 * ⛔ WAS A HARDCODED HISTOGRAM: [4,9,17,22,19,26,31,24,18,21,14,8] — 213 calls, peaking at
 * 31 at noon. It drove "Calls today", "Busiest hour 12p · 31 calls", and the hours-saved and
 * dollars-saved figures beneath them. A carrier with an empty call history read "213 calls
 * today" on the first tab and "No calls yet" on the fourth.
 *
 * Built from the real call rows now. 6am-6pm in the viewer's own zone, which is the window
 * the panel claims to show.
 */
const HOURLY_LABELS = ["6a", "7a", "8a", "9a", "10a", "11a", "12p", "1p", "2p", "3p", "4p", "5p"];
const HOURLY_START = 6;
function hourlyFromCalls(calls) {
  const bins = new Array(HOURLY_LABELS.length).fill(0);
  let outside = 0;
  for (const c of calls || []) {
    const when = c.startedAt || c.createdAt;
    if (!when || c.day !== 'today') continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const slot = d.getHours() - HOURLY_START;
    if (slot >= 0 && slot < bins.length) bins[slot] += 1; else outside += 1;
  }
  return { bins, outside, total: bins.reduce((a, b) => a + b, 0) + outside };
}

const STATUS = {
  needs_driver: { label: "Needs driver", muted: true },
  calling: { label: "On the line", fill: true, live: true },
  rolling: { label: "Rolling" },
  attention: { label: "Needs attention", fill: true },
  empty: { label: "Empty and available" },
  delivered: { label: "Delivered", muted: true },
  canceled: { label: "Canceled", muted: true },
  tonu: { label: "TONU", muted: true },
  // ⛔ THE TWO THE DATABASE ACTUALLY USES AND THIS MAP DID NOT HAVE.
  // `schema.sql`: `status TEXT NOT NULL DEFAULT 'planned'`, documented as
  // planned | rolling | delivered | cancelled. So the DEFAULT status of every imported load
  // was missing, and `cancelled` was spelled `canceled` here — one 'l' apart. `Chip` did an
  // unguarded `STATUS[status].fill`, so the first load a carrier saved took the tab down.
  planned: { label: "Planned", muted: true },
  cancelled: { label: "Cancelled", muted: true },
};

/**
 * The chip metadata for a status — never undefined.
 *
 * An unknown status is shown verbatim rather than swallowed: if the backend grows a status
 * this screen has not met, a dispatcher should see the word, not a blank chip, and
 * certainly not a white screen.
 */
export function statusMeta(status) {
  const key = String(status || '').trim();
  if (Object.hasOwn(STATUS, key)) return STATUS[key];
  return { label: key || 'Unknown', muted: true };
}

const LIMITS = [
  { t: "Negotiate or discuss rate", d: "Alfred never quotes, counters, or accepts a rate. Any rate conversation goes to the office." },
  { t: "Commit to an appointment", d: "Alfred reports appointment times and flags late risk. It doesn't agree to new ones — a person does." },
  { t: "Authorize money", d: "No advances, lumper payments, Comdata codes, or reimbursements. Alfred captures the amount and who's asking, then stops." },
  { t: "Accept or decline a load on the carrier's behalf", d: "Alfred can brief a driver on a load. It can't book, tender, or turn one down." },
  { t: "Speak for the carrier on an incident or claim", d: "Accident, injury, cargo damage, or a DOT stop ends the call plan and puts a person on the line. Alfred asks about injuries and safety, nothing else." },
  { t: "Give legal, medical, or hours-of-service advice", d: "Alfred reports what the ELD says. It never tells a driver they can keep driving." },
  { t: "Hide that it's an AI", d: "Every call opens with the disclosure, and Alfred repeats it any time it's asked." },
];

const TOGGLEABLE = [
  { id: "cell", label: "Share driver cell numbers with brokers", on: false, note: "Off by default. Brokers get the office number." },
  { id: "record", label: "Record calls", on: true, note: "Disclosure plays on every call. Two-party-consent states are always disclosed." },
  // ⛔ ADVERTISED AS ON, AND CANNOT FIRE. The check needs an ELD, and no ELD is connected,
  // so `inMotion()` is false for every driver. A safety control that is switched on and does
  // nothing is worse than one that is plainly unavailable.
  { id: "motion", label: "Never call a driver in motion", on: false, note: "Needs an ELD connected. Until then Alfred cannot tell whether a truck is moving, so this cannot be enforced." },
  { id: "quiet", label: "Respect per-driver quiet hours", on: true, note: "Uses the driver's home time zone, not the office's." },
  { id: "late", label: "Escalate late risk before the window closes", on: true, note: "Compares ETA against the appointment and hands off early rather than reporting a miss." },
];

/**
 * ⛔ FOUR OF THESE SHIPPED AS `connected`, WITH MASKED KEYS AND "last sync 1 min ago".
 *
 * McLeod, Motive, Samsara and MacroPoint all showed as live. On the Drivers tab the same app
 * correctly said "no ELD connected — position and hours are not available for this driver".
 * A carrier comparing the two tabs would conclude one of them was broken, and the one they
 * would trust is the one that is wrong.
 *
 * There is no integration code behind any of these yet, so they are all off.
 */
const INTEGRATIONS = [
  { id: "mcleod", name: "McLeod LoadMaster", kind: "TMS", state: "off", pulls: "Load numbers, stops, appointments, driver assignments", keyHint: "lm_live_…", where: "In McLeod: Utilities → API Access → key with dispatch read scope." },
  { id: "trimble", name: "Trimble TMW", kind: "TMS", state: "off", pulls: "Loads, stops, and driver assignments", keyHint: "tmw_…", where: "In TMW: Admin → Integrations → API keys." },
  { id: "rose", name: "Rose Rocket", kind: "TMS", state: "off", pulls: "Loads, stops, customers", keyHint: "rr_…", where: "In Rose Rocket: Settings → Developers → create a token." },
  { id: "motive", name: "Motive", kind: "ELD", state: "off", pulls: "Position, speed, duty status, hours remaining", keyHint: "mv_…", where: "In Motive: Admin → Integrations → API keys. Read scope on vehicles and HOS." },
  { id: "samsara", name: "Samsara", kind: "ELD", state: "off", pulls: "Position, speed, duty status, hours remaining", keyHint: "samsara_api_…", where: "In Samsara: Settings → Cloud API tokens." },
  { id: "macropoint", name: "MacroPoint", kind: "Visibility", state: "off", pulls: "Pushes tracking to brokers who ask for it instead of calls", keyHint: "mp_…", where: "Your broker usually provisions this. Ask for a carrier API credential." },
  { id: "truckertools", name: "Trucker Tools", kind: "Visibility", state: "off", pulls: "Pushes tracking to brokers who ask for it instead of calls", keyHint: "tt_…", where: "Request a carrier key from Trucker Tools support." },
  { id: "hubspot", name: "HubSpot", kind: "CRM", state: "off", pulls: "Broker and shipper contacts, so Alfred knows who it's calling", keyHint: "pat-na1-…", where: "In HubSpot: Settings → Integrations → Private apps." },
  { id: "sheets", name: "Google Sheets", kind: "CRM", state: "off", pulls: "A shared sheet of loads, if that's what you run on today", keyHint: "Paste a share link", where: "Share the sheet with the service account shown after connecting." },
];

const LOW_CONFIDENCE = ["puWindow", "commodity"];

/* -------------------------------- helpers ---------------------------------- */

const nowLabel = () => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
/**
 * ⛔ `attempts: {first, second, third}` AND `avgAnswerSec` DO NOT EXIST.
 *
 * They were seed fields invented to fill a performance panel, and the mapper returns null
 * for both (see fromApi.js). `totalAttempts(null)` threw, and — worse — `firstRate(null)`
 * returned 0 via coercion, so every real driver rendered "0%" and was listed under "needs
 * repeat callbacks". A fabricated statistic about a named employee is the last thing this
 * screen should produce.
 *
 * What the database DOES hold is the call history, so the numbers are computed from it.
 * The metric changes honestly with the data: not "first-attempt connect rate", which
 * nothing records, but "answered this many of the calls we placed".
 */
function callStats(calls, driverId) {
  // ⛔ A SEEDED CALL IS NOT A MEASUREMENT. These figures are claims about what the platform
  // did — "answered 25% of 4 calls that rang", "avg. time on the line 2:00", and at its worst
  // "Luz Herrera sits answering fewer than 70% of the calls we place", a named employee. The
  // demo seed writes a completed call, a refused one and three no-answers, and every one of
  // those numbers was computed from them: the exact fabricated statistic the note above says
  // is the last thing this screen should produce. An independent review found it.
  //
  // Excluded, not marked: there is no honest way to show an answer rate "including the
  // examples". A board with nothing but seeded calls reports no rate at all, which is true.
  const mine = (calls || []).filter((c) => c.driverId === driverId && !c.seeded);
  // ⛔ `failed` IS NOT THE DRIVER'S FAULT AND IS EXCLUDED FROM THE RATE. A failed row is one
  // the backend wrote when the phone never rang — `not_configured`, `quiet_hours`,
  // `from_number_not_found`, `unreachable`. Counting those against the driver meant that on
  // a box with no API key every real employee read 0% and got named on screen as somebody
  // who "sits below 70%". It is reported separately instead.
  const settled = mine.filter((c) => ['completed', 'no_answer'].includes(c.status));
  const answered = mine.filter((c) => c.status === 'completed');
  const durs = answered.map((c) => c.dur).filter((v) => v != null);
  return {
    total: mine.length,
    settled: settled.length,
    answered: answered.length,
    noAnswer: mine.filter((c) => c.status === 'no_answer').length,
    failed: mine.filter((c) => c.status === 'failed').length,
    // null, not 0, when nothing has settled — "no calls yet" is not "never answers".
    rate: settled.length ? answered.length / settled.length : null,
    avgSec: durs.length ? Math.round(durs.reduce((a, b) => a + b, 0) / durs.length) : null,
  };
}

/** A rate as a percentage, or an em dash. Never "0%" for an absent measurement. */
const pct = (v) => (v == null ? "\u2014" : `${Math.round(v * 100)}%`);

/**
 * Is this driver's truck moving?
 *
 * ⛔ UNKNOWN IS NOT "MOVING". There is no ELD integration, so `driver.eld` is null for every
 * real driver and `driver.eld.inMotion` threw in eight places. False is also the safe
 * default for the one decision this gates — whether to hold a call — because the platform
 * enforces quiet hours itself, so the worst case is a call it would have placed anyway.
 */
const inMotion = (d) => !!(d && d.eld && d.eld.inMotion);
const waitLabel = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
// ⛔ JOINED, NOT INTERPOLATED, AND EXPORTED SO THEY CAN BE TESTED.
//
// These two lines used to be `${driver.truck} · ${driver.trailer}` and
// `${driver.tz} · quiet ${driver.quiet}`. A driver imported from a CSV has a name and a phone
// number and nothing else, which is the commonest shape there is — and a template literal
// turns each absent field into the four-character word "null" on screen. React drops a null
// CHILD, so the roster rows beside these were fine and only the profile was wrong.
//
// They are functions rather than inline expressions because the panels they live in are
// COLLAPSED by default: `Disclosure` renders `{open && children}`, so a server render never
// reaches them and a test that renders the Inspector cannot see them. As functions they are
// testable directly, which is the only honest way to cover them.
export const truckLine = (d) => [d?.truck, d?.trailer].filter(Boolean).join(" · ") || "Not on file";
export const zoneLine = (d) => [d?.tz, d?.quiet && `quiet ${d.quiet}`].filter(Boolean).join(" · ") || "Not on file";
// ⛔ THE DROPDOWN AND THE NAME MUST COME FROM THE SAME LIST. `staffById` is called from
// seven places, several of them module-level helpers outside any component, so threading
// live state through all of them would mean rewriting a lot of working interface. Instead
// the fetch publishes into this registry and the lookup is unchanged.
//
// Without it the seat picker listed the carrier's REAL dispatchers while every name it
// rendered came from the hard-coded STAFF constant — pick Renee, and the board tells the
// driver to ask for somebody who does not work there.
let STAFF_LIVE = [];
export function setStaffRegistry(rows) { STAFF_LIVE = Array.isArray(rows) ? rows : []; }
/** The live dispatcher list, for any picker that needs to offer real people. */
const staffList = () => STAFF_LIVE;
/**
 * ⛔ COMPARED AS STRINGS, DELIBERATELY. Dispatcher ids are Postgres SERIAL integers, and
 * every id that comes back out of a `<select>` is a string. `s.id === id` is `5 === "5"`,
 * which is false — so picking a seat resolved to null, `me` was null, and every "Mark
 * handled" button answered "Only ops can clear this one."
 */
const staffById = (id) => (id == null ? null : STAFF_LIVE.find((s) => String(s.id) === String(id)) || null);
const durLabel = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const firstPickup = (l) => { const st = (l && l.stops) || []; return st.find((s) => s.type === "pickup") || st[0] || null; };
const lastDrop = (l) => { const st = (l && l.stops) || []; return [...st].reverse().find((s) => s.type === "drop") || st[st.length - 1] || null; };
/**
 * "Houston → Laredo +1".
 *
 * ⛔ `stops` IS JSONB WITH NO SHAPE CONSTRAINT (`DEFAULT '[]'`). An empty list made
 * `firstPickup(l)` undefined and this read `.city` off it; a stop object without a `city`
 * died on `.split`. Both are legal rows, and both took the tab down.
 */
export const laneLabel = (l) => {
  const stops = (l && Array.isArray(l.stops)) ? l.stops : [];
  const town = (stop) => {
    const city = stop && stop.city;
    if (city) return String(city).split(",")[0].trim();
    return (stop && stop.name) ? String(stop.name) : null;
  };
  const a = town(firstPickup({ stops }));
  const b = town(lastDrop({ stops }));
  const extra = stops.length - 2;
  const tail = extra > 0 ? ` +${extra}` : "";
  if (a && b && a !== b) return `${a} → ${b}${tail}`;
  if (a || b) return `${a || b}${tail}`;
  return "No stops yet";
};
// ⛔ BOTH SPELLINGS. The screen said `canceled`; the schema says `cancelled`, so a real
// cancelled load counted as active and sat on the board forever.
const CLOSED = ["delivered", "canceled", "cancelled", "tonu"];

function parseClock(t) {
  const m = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(t || "");
  if (!m) return null;
  let h = parseInt(m[1], 10) % 12;
  if (/pm/i.test(m[3])) h += 12;
  return h * 60 + parseInt(m[2], 10);
}

function inQuietHours(at, driver) {
  const mins = parseClock(at);
  if (mins === null) return false;
  const local = (mins + driver.tzShift * 60 + 1440) % 1440;
  return local < 6 * 60 || local >= 21 * 60;
}

function localTimeFor(at, driver) {
  const mins = parseClock(at);
  if (mins === null || driver.tzShift === 0) return null;
  const local = (mins + driver.tzShift * 60 + 1440) % 1440;
  const h = Math.floor(local / 60), m = local % 60;
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"} ${driver.tz}`;
}

/** ETA past the appointment close, on any stop that isn't done yet */
function lateRisk(load) {
  for (const s of load.stops) {
    if (s.state === "complete" || s.state === "canceled") continue;
    const eta = parseClock(s.etaAt), end = parseClock(s.windowEnd);
    if (eta === null || end === null) continue;
    if (eta > end) return { stop: s, minutes: eta - end };
  }
  return null;
}

/** not enough drive time left to make the next stop */
function clockPressure(load, driver) {
  if (!load.hoursToNextStop || CLOSED.includes(load.status)) return null;
  // No ELD, no hours — and no warning invented from hours we cannot see.
  if (!driver || !driver.eld || driver.eld.driveLeft == null) return null;
  if (driver.eld.driveLeft >= load.hoursToNextStop) return null;
  return { need: load.hoursToNextStop, have: driver.eld.driveLeft };
}

function routeContact(load) {
  const p = staffById(load.contact1), s = staffById(load.contact2);
  if (p && p.onHours) return { person: p, fallback: false };
  if (s) return { person: s, fallback: true, primary: p };
  return { person: p, fallback: false };
}

const openDetention = (l) => l.stops.find((s) => s.state === "arrived" && s.arrivedAt);

/* ------------------------------- primitives -------------------------------- */

function Micro({ children, style }) {
  return <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
}
function Tag({ children, solid }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.ink}`, background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}
function Soft({ children }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.hair}`, color: T.ink2, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}
function Chip({ status }) {
  const s = statusMeta(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 500, padding: "3px 9px", borderRadius: 4, whiteSpace: "nowrap",
      background: s.fill ? T.ink : "transparent", color: s.fill ? "#fff" : s.muted ? T.ink3 : T.ink, border: `1px solid ${s.fill ? T.ink : s.muted ? T.hair : T.ink}` }}>
      {s.live && <span className="alf-dot" />}{s.label}
    </span>
  );
}
function Button({ children, onClick, kind = "primary", disabled, size = "md", title }) {
  const k = { primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` }, quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` } }[kind];
  return (
    <button onClick={onClick} disabled={disabled} title={title} className="alf-btn" style={{
      ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15,
      padding: size === "sm" ? "6px 11px" : "8px 15px", borderRadius: 7, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1 }}>
      {children}
    </button>
  );
}
function Toggle({ on, onChange }) {
  return (
    <button onClick={() => onChange(!on)} aria-pressed={on} style={{ width: 42, height: 24, borderRadius: 999, padding: 2, cursor: "pointer", flexShrink: 0,
      background: on ? T.ink : "#fff", border: `1px solid ${on ? T.ink : T.hair}`, display: "flex", justifyContent: on ? "flex-end" : "flex-start", transition: "background .18s" }}>
      <span style={{ width: 18, height: 18, borderRadius: 999, background: on ? "#fff" : T.ink4, transition: "all .18s" }} />
    </button>
  );
}
function Disclosure({ title, meta, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <button onClick={() => setOpen((o) => !o)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", padding: "14px 20px", cursor: "pointer", textAlign: "left", font: "inherit" }}>
        <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {meta && <span style={{ fontSize: 12.5, color: T.ink3 }}>{meta}</span>}
          <span style={{ color: T.ink3, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
        </span>
      </button>
      {open && <div style={{ padding: "0 20px 18px" }}>{children}</div>}
    </div>
  );
}
function Field({ label, value, isMono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "6px 0" }}>
      <span style={{ fontSize: 13, color: T.ink3 }}>{label}</span>
      <span style={{ fontSize: 13, textAlign: "right", fontFamily: isMono ? mono : sans, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}
function Waveform({ active, invert }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2.5, height: 14 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={active ? "alf-bar" : ""} style={{ width: 2, borderRadius: 1, background: invert ? "#fff" : T.ink, height: active ? 14 : 4, animationDelay: `${i * 0.11}s`, opacity: active ? 1 : 0.3 }} />
      ))}
    </span>
  );
}
function Panel({ children }) {
  return <div style={{ background: T.surface, border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>{children}</div>;
}
function PanelHead({ title, note, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "16px 20px 14px", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{title}</div>
        {note && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>{note}</div>}
      </div>
      {right}
    </div>
  );
}
function Segmented({ value, onChange, options }) {
  return (
    <div style={{ display: "flex", border: `1px solid ${T.hair}`, borderRadius: 7, overflow: "hidden", flexWrap: "wrap" }}>
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} style={{ font: "inherit", fontSize: 12.5, fontWeight: 500, padding: "6px 11px", border: "none", cursor: "pointer",
          background: value === o.v ? T.ink : "#fff", color: value === o.v ? "#fff" : T.ink2 }}>{o.label}</button>
      ))}
    </div>
  );
}
const selectStyle = { font: "inherit", fontSize: 13, background: "#fff", color: T.ink, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "6px 10px", outline: "none", appearance: "none", cursor: "pointer" };

function Metric({ label, value, sub, emphasis }) {
  return (
    <div style={{ flex: "1 1 165px", padding: "18px 20px", borderRight: `1px solid ${T.hairSoft}` }}>
      <Micro>{label}</Micro>
      <div style={{ fontFamily: mono, fontVariantNumeric: "tabular-nums", fontSize: 32, letterSpacing: -1.5, marginTop: 8, lineHeight: 1, fontWeight: emphasis ? 600 : 400 }}>{value}</div>
      {sub && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 7 }}>{sub}</div>}
    </div>
  );
}
/** Answered / no answer / refused, from real calls. Empty track when nothing has settled. */
function AnswerBar({ s }) {
  // ⛔ NOT `s.settled` — that deliberately excludes `failed` so a platform refusal cannot
  // count against the driver's answer rate. The BAR still shows those, so it must divide by
  // everything it paints or the segments overflow past 100%.
  const total = s.answered + s.noAnswer + s.failed;
  return (
    <span style={{ display: "flex", width: "100%", height: 6, borderRadius: 3, overflow: "hidden", background: T.hairSoft }}>
      {total > 0 && [{ v: s.answered, f: T.ink }, { v: s.noAnswer, f: "#9E9E9E" }, { v: s.failed, f: "#DCDCDC" }]
        .map((x, i) => <span key={i} style={{ width: `${(x.v / total) * 100}%`, background: x.f }} />)}
    </span>
  );
}
function Legend({ fill, label }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.ink3 }}>
    <span style={{ width: 9, height: 9, background: fill, borderRadius: 2, border: fill === "#DCDCDC" ? `1px solid ${T.hair}` : "none" }} />{label}</span>;
}
function EldLine({ driver }) {
  // ⛔ NO TELEMATICS, NO POSITION. The seeds carried "I-10 near Winnie, TX" at 64 mph and a
  // drive-hours clock. None of it is in the database, and a dispatcher who believes an
  // invented truck position will route around a truck that is somewhere else.
  if (!driver || !driver.eld) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: T.ink3 }}>
        <Soft>no ELD connected</Soft>
        <span>Position and hours are not available for this driver.</span>
      </div>
    );
  }
  const e = driver.eld;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: T.ink2 }}>
      {e.inMotion ? <Tag solid>in motion</Tag> : <Soft>stopped</Soft>}
      {e.position && <span>{e.position}</span>}
      {e.driveLeft != null && <span style={{ fontFamily: mono, color: T.ink3 }}>{e.driveLeft}h drive left</span>}
      {e.provider && <span style={{ fontSize: 11.5, color: T.ink4 }}>{e.provider}{e.updated != null ? `, ${e.updated}m ago` : ""}</span>}
    </div>
  );
}

function DetentionClock({ stop, broker }) {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(id); }, []);
  const mins = Math.floor((Date.now() - stop.arrivedAt) / MIN);
  const over = mins >= 120;
  return (
    <div style={{ border: `1px solid ${over ? T.ink : T.hair}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        <Micro>Detention clock</Micro>
        {over && <Tag solid>billable</Tag>}
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 18, fontVariantNumeric: "tabular-nums" }}>{Math.floor(mins / 60)}h {String(mins % 60).padStart(2, "0")}m</span>
      </div>
      <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 7, lineHeight: 1.5 }}>
        On site at {stop.name} since gate-in. {over ? `Free time ended at 2h — ${mins - 120} billable minutes and counting. ${broker.name} was notified at the mark.` : `Free time ends at 2h. Alfred notifies ${broker.name} when it does.`}
      </div>
    </div>
  );
}

/* ------------------------------- paperwork --------------------------------- */

function Paperwork({ load }) {
  const got = load.paperwork.filter((d) => d.v).length;
  return (
    <div>
      {load.paperwork.length === 0 && <div style={{ fontSize: 13, color: T.ink3 }}>Nothing captured on this load.</div>}
      {load.paperwork.map((d, i) => (
        <div key={d.k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
          <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 110 }}>{d.k}</span>
          <span style={{ flex: 1, fontFamily: d.v ? mono : sans, fontSize: 13, color: d.v ? T.ink : T.ink3 }}>{d.v || "not captured yet"}</span>
          {d.v ? <span style={{ fontSize: 12, color: T.ink3 }}>{d.at}</span> : <Soft>on the next call</Soft>}
        </div>
      ))}
      {load.paperwork.length > 0 && (
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 11, lineHeight: 1.55, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 11 }}>
          {got} of {load.paperwork.length} captured by voice. Alfred reads every number back to the driver and files it only after they confirm. Physical copies still come in the way they do today — Alfred doesn't handle images.
        </div>
      )}
    </div>
  );
}

/* ------------------------------ recording ---------------------------------- */

/**
 * A call recording.
 *
 * ⛔ THIS USED TO BE A FAKE. It drew 44 bars from a seeded PRNG over the row's id and
 * animated a playhead across them on a 90ms timer — a Play button that produced no sound
 * and a waveform of nothing. It is now a real audio element, and the board only renders it
 * when a row actually carries a URL (today: never, there is no recording endpoint for a
 * freight leg). Kept real so that wiring a URL in later makes it work, rather than making
 * the fake look true.
 */
function Recording({ src, dur }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${T.hair}`, borderRadius: 8 }}>
      <audio controls preload="none" src={src} style={{ flex: 1, minWidth: 0, height: 32 }} />
      {dur != null && <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink2, minWidth: 44, textAlign: "right" }}>{durLabel(dur)}</span>}
    </div>
  );
}

/* -------------------------------- history ---------------------------------- */

/* one row of call history — used by the History tab and by a driver's profile */
/**
 * ⛔ THIS ROW WAS WRITTEN AGAINST HAND-MADE CALLS AND BROKE FOUR WAYS ON REAL ONES.
 *   · `c.transcript.map(...)` — the column is a string, not a list of turns. Parsed in
 *     fromApi.js now; an unattributed line shows its text with no speaker rather than
 *     being credited to Alfred.
 *   · `<Recording seed={parseInt(c.id.slice(1))} …>` — a Play button over a waveform
 *     generated from the row's id. There is no recording endpoint for a freight leg, so
 *     the control promised audio that cannot exist. It is gone until there is a URL.
 *   · `c.captured.map(...)` — the "BOL / Seal / weight" chips were typed by a person.
 *     Nothing extracts fields from a freight transcript yet, so the strip hides.
 *   · `durLabel(c.dur)` / `c.at` / `c.loadRef` — all null on a call that was written but
 *     never placed, which is exactly the row a dispatcher most needs to see.
 */
/**
 * ⛔ WHAT A SEEDED CALL MUST SAY. This board's `driver_calls` rows are the only thing on it
 * that assert an EVENT happened — Alfred phoned this driver and here is what was said — and a
 * demonstration needs a finished call to show, because a live one cannot be relied on in front
 * of a room. So the row is allowed and it says what it is, in both places a reader looks: the
 * collapsed row, and inside the transcript, which is the part that reads as real.
 *
 * The carrier board's source used to carry seven invented calls with full transcripts,
 * including a tire blowout — removed because they existed to make a screenshot look busy. This
 * is the same data with the one thing those were missing.
 */
export const SEEDED_CALL = "Seeded for a demonstration — this call was not placed.";

/** A carrier that is not live says so on its board: who Alfred will and will not call (backend stage.mjs). */
export const STAGE_NOTICE = {
  setup: ["Your account is still being set up.", "Alfred calls nobody yet."],
  rehearsal: ["Rehearsal.", "Alfred calls only the test phones MuleWorks marked. Your drivers and applicants are called once you go live."],
  paused: ["Your account is paused.", "Alfred calls nobody until it is resumed."],
};

export function CallRow({ c, drivers, loads, open, onToggle, showDriver = true }) {
  const d = c.driverId ? drivers.find((x) => x.id === c.driverId) : null;
  const load = c.loadRef ? loads.find((l) => l.ref === c.loadRef) : (c.loadId ? loads.find((l) => l.id === c.loadId) : null);
  const settled = c.status === 'completed' || c.status === 'no_answer' || c.status === 'failed';
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <button onClick={onToggle} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
        <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, minWidth: 62 }}>{c.at || "—"}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{c.step}</span>
            <Soft>{c.party}</Soft>
            {c.outcome !== "Completed" && <Tag solid>{c.outcome}</Tag>}
            {c.seeded && <Soft>seeded example</Soft>}
            {c.persona && <Soft>AI persona</Soft>}
          </span>
          <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
            {c.loadRef || "No load"}{showDriver ? ` · ${d ? [d.name, d.truck].filter(Boolean).join(" · ") : "Driver no longer on the roster"}` : load ? ` · ${laneLabel(load)}` : ""}
          </span>
        </span>
        <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3 }}>{c.dur == null ? "" : durLabel(c.dur)}</span>
        <span style={{ color: T.ink4, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
      </button>
      {open && (
        <div className="alf-fade" style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {c.error && (
            <div style={{ fontSize: 13, color: T.ink2, border: `1px solid ${T.hair}`, borderLeft: `3px solid ${T.ink}`, borderRadius: 4, padding: "9px 11px" }}>
              The platform refused this call: <span style={{ fontFamily: mono, fontSize: 12.5 }}>{c.error.replace(/_/g, " ")}</span>.
              {c.error === "not_configured" && " Nobody has connected the voice platform to this app yet."}
            </div>
          )}
          {c.recordingUrl && <Recording src={c.recordingUrl} dur={c.dur} />}
          {c.captured.length > 0 && (
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              {c.captured.map((x, i) => <span key={i} style={{ fontSize: 12, border: `1px solid ${T.hair}`, borderRadius: 4, padding: "3px 8px", color: T.ink2 }}>{x}</span>)}
            </div>
          )}
          {/* ⛔ BEFORE THE TRANSCRIPT, not after it. The transcript is what reads as real, and
              a marker underneath it is a marker somebody has already believed the words above. */}
          {c.seeded && (
            <div style={{ fontSize: 13, color: T.ink, border: `1px solid ${T.ink}`, borderRadius: 6, padding: "9px 11px", lineHeight: 1.45 }}>
              {SEEDED_CALL} The words below are a written example of what Alfred captures.
            </div>
          )}
          <div>
            <Micro style={{ marginBottom: 8 }}>Transcript</Micro>
            {c.transcript.length === 0 ? (
              <div style={{ fontSize: 13, color: T.ink3 }}>
                {settled ? "No transcript for this call." : "Not finished yet — the transcript appears once the call ends."}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {c.transcript.map((l, i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", color: l.who === "alfred" ? T.ink : T.ink4, minWidth: 42, paddingTop: 3 }}>
                      {l.speaker || l.who || ""}
                    </span>
                    <span style={{ fontSize: 13.5, lineHeight: 1.45, color: l.who === "alfred" ? T.ink : T.ink2 }}>{l.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Call history, from `driver_calls` — every call this carrier asked for.
 *
 * ⛔ IT USED TO RENDER `CALL_LOG`: seven calls with full invented transcripts, including a
 * tire blowout and a detention clock. They were there to make a screenshot look busy, and
 * they showed identically on a demo to a carrier whose own roster was empty — so the one
 * screen that is supposed to prove Alfred made the calls was the screen proving nothing.
 *
 * An empty list now has three different meanings and says which: the seam is not connected,
 * nothing has been called yet, or the filters exclude everything.
 */
export function History({ drivers, loads, calls, loaded, configured, onRefresh, refreshing }) {
  const [day, setDay] = useState("all");
  const [driverId, setDriverId] = useState("all");
  const [loadRef, setLoadRef] = useState("all");
  const [openId, setOpenId] = useState(null);

  // ⛔ COMPARED AS STRINGS. `c.driverId` is the backend's SERIAL integer; every value out
  // of a `<select>` is a string, so `7 === "7"` was false and the filter could never match
  // — the screen said "No calls match those filters" for every driver. My own new tests
  // missed it because their fixtures used string driver ids ('d2'), the one shape the real
  // API never returns.
  const rows = calls.filter((c) => (day === "all" || c.day === day)
    && (driverId === "all" || String(c.driverId) === String(driverId))
    && (loadRef === "all" || c.loadRef === loadRef));
  const refs = Array.from(new Set(calls.map((c) => c.loadRef).filter(Boolean)));
  // Only calls that actually connected have a duration, so talk time sums those.
  // ⛔ AND ONLY THE ONES THAT WERE PLACED. This summed every row, so a list holding one real
  // 3:00 call and a 2:00 seeded one reported "5:00 of talk time" in the same sentence that
  // credits the platform with recording it. A seeded call has a duration because the seed
  // writes started_at and ended_at — that is what makes the demonstration look like a call,
  // and it is exactly why it must not be added up.
  const talk = rows.filter((c) => !c.seeded).reduce((s, c) => s + (c.dur || 0), 0);
  const filtered = driverId !== "all" || loadRef !== "all" || day !== "all";
  // ⛔ COUNTED, NOT HIDDEN. A seeded call is in the list on purpose — the demonstration is what
  // it is for — but the sentence above the list must not fold it into "every call is recorded
  // by the platform", which asserts the platform heard it.
  const seededShown = rows.filter((c) => c.seeded).length;

  const note = !loaded ? "Loading…"
    : calls.length === 0
      ? (configured ? "No calls yet. Call a driver from a load and it appears here." : "The voice platform is not connected to this app yet, so no calls can be placed.")
      : `${rows.length} call${rows.length === 1 ? "" : "s"}${seededShown ? ` (${seededShown} seeded for a demonstration)` : ""}${talk ? ` · ${durLabel(talk)} of talk time` : ""}. ${seededShown === rows.length ? "The seeded ones were never placed." : "Every call the platform placed is recorded and transcribed when it ends."}`;

  return (
    <Panel>
      <PanelHead title="Call history" note={note}
        right={
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {onRefresh && (
              <button onClick={onRefresh} disabled={refreshing} style={{ font: "inherit", fontSize: 12.5, background: "none", border: `1px solid ${T.hair}`, borderRadius: 5, padding: "3px 9px", cursor: refreshing ? "default" : "pointer", color: refreshing ? T.ink3 : T.ink }}>
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            )}
            <Segmented value={day} onChange={setDay} options={[{ v: "today", label: "Today" }, { v: "yesterday", label: "Yesterday" }, { v: "all", label: "All" }]} />
          </span>
        } />
      {calls.length > 0 && (
        <div style={{ display: "flex", gap: 8, padding: "0 20px 14px", flexWrap: "wrap" }}>
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)} style={selectStyle}>
            <option value="all">All drivers</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={loadRef} onChange={(e) => setLoadRef(e.target.value)} style={selectStyle}>
            <option value="all">All loads</option>
            {refs.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {filtered && (
            <button onClick={() => { setDriverId("all"); setLoadRef("all"); setDay("all"); }} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Clear</button>
          )}
        </div>
      )}
      {rows.length === 0
        ? <div style={{ padding: "26px 20px 30px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13.5, color: T.ink3 }}>
            {!loaded ? "Loading calls…" : calls.length === 0 ? (configured ? "No calls have been placed for this carrier yet." : "Connect the voice platform and the calls you place will be listed here.") : "No calls match those filters."}
          </div>
        : rows.map((c) => <CallRow key={c.id} c={c} drivers={drivers} loads={loads} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)} />)}
    </Panel>
  );
}

/* -------------------------------- overview --------------------------------- */

function QueueItem({ q, drivers, loads, seat, onOpenLoad, onResolve }) {
  // ⛔ `d` MAY BE MISSING. driver_calls.driver_id is ON DELETE SET NULL, so deleting a
  // driver leaves their failed calls behind with no driver attached. This used to be read
  // unguarded, and Overview is the first screen the dashboard opens on.
  const d = drivers.find((x) => x.id === q.driverId) || null;
  const load = q.loadRef ? loads.find((l) => l.ref === q.loadRef) : null;
  const need = q.kind === "money" ? MONEY_KIND[q.money].to : NEEDS[q.kind];
  const me = staffById(seat);
  const allowed = me && me.can.includes(need);
  const urgent = q.kind === "incident" || q.waiting > 30;

  return (
    <div style={{ display: "flex", gap: 14, padding: "16px 20px", borderTop: `1px solid ${T.hairSoft}`, position: "relative" }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: urgent ? T.ink : T.hair }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          <Tag solid={q.kind === "incident"}>{KIND[q.kind]}</Tag>
          {/* ⛔ ON THE ROW, because a handoff is a job somebody is about to do. The panel's
              note says how many are seeded; this says WHICH. */}
          {q.seeded && <Soft>seeded example</Soft>}
          {q.kind === "money" && <Soft>{MONEY_KIND[q.money].label}</Soft>}
          {q.amount && <span style={{ fontFamily: mono, fontSize: 12.5 }}>{q.amount}</span>}
          {q.loadRef && <button onClick={() => onOpenLoad(q.loadRef)} style={{ font: "inherit", fontFamily: mono, fontSize: 12, color: T.ink2, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>{q.loadRef}</button>}
          <span style={{ fontSize: 12.5, color: T.ink3 }}>{d ? [d.name, d.truck].filter(Boolean).join(" · ") : "Driver no longer on the roster"}</span>
          <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: urgent ? T.ink : T.ink3 }}>waiting {waitLabel(q.waiting)}</span>
        </div>

        <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.25, marginTop: 9 }}>{q.title}</div>

        {q.quote && (
          <div style={{ borderLeft: `2px solid ${T.hair}`, paddingLeft: 12, marginTop: 10 }}>
            <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{q.quote}</div>
            <Micro style={{ marginTop: 5 }}>Driver, on the call</Micro>
          </div>
        )}

        {q.intake && (
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: "12px 14px", marginTop: 12 }}>
            <Micro style={{ marginBottom: 8 }}>What Alfred got before it stopped</Micro>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 26px" }}>
              {q.intake.map(([k, v]) => (
                <span key={k} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 150 }}>
                  <span style={{ fontSize: 11.5, color: T.ink3 }}>{k}</span>
                  <span style={{ fontSize: 13 }}>{v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginTop: 10 }}>{q.ask}</div>
        {/* ⛔ AND BEFORE THE OPERATOR ACTS ON IT. `q.ask` is the instruction — "Somebody needs
            to reach them another way" — and acting on a call that was never placed means
            phoning a driver about nothing, or worse, a fictional number. */}
        {q.seeded && (
          <div style={{ fontSize: 12.5, color: T.ink, border: `1px solid ${T.ink}`, borderRadius: 6, padding: "8px 10px", marginTop: 10, lineHeight: 1.45 }}>
            This was derived from a call seeded for a demonstration. Nobody was rung, and there
            is nothing here for a person to chase.
          </div>
        )}

        {/*
          ⛔ "Ringing now: <name>" IS GONE TOO. It named a person off `load.contact1` and said
          they were being rung. Nothing rings them — see the note where LADDER used to be —
          and it was unreachable only because loadFromApi never produced contact1, so the
          first screen to persist that field would have started the screen lying.
        */}

        <div style={{ display: "flex", gap: 8, marginTop: 13, alignItems: "center", flexWrap: "wrap" }}>
          {q.loadRef && <Button size="sm" onClick={() => onOpenLoad(q.loadRef)}>Open the load</Button>}
          <Button size="sm" kind="quiet" disabled={!allowed} onClick={() => allowed && onResolve(q.id)}>Mark handled</Button>
          {!allowed && <span style={{ fontSize: 12, color: T.ink3 }}>Only {need} can clear this one.</span>}
        </div>
      </div>
    </div>
  );
}

// Exported for Dispatch.render.test.mjs. The board's own render test can only reach the
// pre-fetch state (react-dom/server runs no effects), and every crash this panel has had
// needed POPULATED props — so the test renders this one directly.
export function Overview({ queue, drivers, loads, calls, seat, onOpenLoad, onResolve }) {
  const shown = queue;   // one category, so nothing to filter — see the note on the panel
  const dueNow = loads.reduce((n, l) => n + l.plan.filter((s) => s.state === "due").length, 0);
  const active = loads.filter((l) => !CLOSED.includes(l.status));
  const late = active.map((l) => ({ l, r: lateRisk(l) })).filter((x) => x.r);
  // ⛔ MEASURED ON PLACED CALLS ONLY. Every figure below is a claim about what the platform
  // did — "Calls today 5 · 1 answered" was the headline on the opening tab, computed entirely
  // from the demo seed's five rows, and the histogram, the busiest hour and "Outside 6a-6p"
  // were the same claim in other shapes. An independent review found all of them. The seeded
  // rows are COUNTED beside the figures rather than hidden: the demonstration wants them on
  // screen, it just must not call them measurements.
  const placed = (calls || []).filter((c) => !c.seeded);
  const seededCount = (calls || []).length - placed.length;
  const hourly = hourlyFromCalls(placed);
  const maxHour = Math.max(1, ...hourly.bins);
  const callsToday = hourly.total;
  const answeredToday = placed.filter((c) => c.day === 'today' && c.status === 'completed').length;
  const incidents = queue.filter((q) => q.kind === "incident").length;
  // Busiest hour, only once there is something to be busiest.
  const peakIdx = hourly.bins.indexOf(Math.max(...hourly.bins));
  const peak = hourly.total && hourly.bins[peakIdx] > 0
    ? `${HOURLY_LABELS[peakIdx]} · ${hourly.bins[peakIdx]} call${hourly.bins[peakIdx] === 1 ? "" : "s"}` : "\u2014";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <Metric label="Calls today" value={String(callsToday)}
            sub={callsToday ? `${answeredToday} answered`
              : seededCount ? `None placed — ${seededCount} seeded for a demonstration` : "None placed yet"} />
          <Metric label="Drivers on the roster" value={String(drivers.length)} sub={drivers.length ? `${drivers.filter((d) => d.consent).length} cleared for automated calls` : "Import a roster to begin"} />
          <Metric label="Scheduled calls due" value={String(dueNow)} sub={`Across ${active.length} active load${active.length === 1 ? "" : "s"}`} />
          <Metric label="Waiting on a person" value={String(queue.length)}
            sub={incidents ? `${incidents} is an incident`
              : queue.length ? `Longest waiting ${waitLabel(queue[0].waiting)}`
              : "Nothing handed off"} emphasis />
        </div>
        {/* ⛔ THE "WHAT THAT'S WORTH" STRIP IS GONE.
            It read: "14.9h of dispatcher time back today", "$507 at a $34 loaded hourly
            rate", "4.6h detention billed this week", and "213 of 213 calls handled end to
            end" — alongside a flat "On-time pickup 94%, last 30 days, 71 loads". Every
            number was derived from the hardcoded histogram or typed in by hand. None of
            them is measurable from anything the platform stores: there is no baseline for
            what a call would have cost a dispatcher, no detention billing, and no pickup
            appointment history. A savings figure is exactly the number a carrier would
            repeat to their own boss, so inventing it is the worst one to get wrong. */}
      </Panel>

      {late.length > 0 && (
        <Panel>
          <PanelHead title="Late risk" note="ETA is past the appointment close. Alfred escalates before the window, not after the miss." />
          {late.map(({ l, r }) => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", position: "relative" }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: T.ink }} />
              <span style={{ flex: 1, minWidth: 200 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{r.stop.name}</span>
                  <Tag solid>{waitLabel(r.minutes)} late</Tag>
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
                  ETA {r.stop.etaAt} against a {r.stop.windowEnd} close · {l.broker.name}
                </span>
              </span>
              <Button size="sm" kind="quiet" onClick={() => onOpenLoad(l.ref)}>Open load</Button>
            </div>
          ))}
        </Panel>
      )}

      <Panel>
        {/*
          ⛔ NO FILTER CHIPS. There were six — Incidents, Money, Appointments, Concerns,
          Callbacks — and then a version that derived them from the kinds present. Both were
          dead: `exceptionsFromCalls` only ever emits one kind, so a chip row is either tabs
          guaranteed to be empty or a row that never renders. A one-category list does not
          need a filter.
        */}
        <PanelHead title="Waiting on a person"
          note={queue.length === 0
            ? "A call the platform refused, and a driver who has stopped answering. Derived from what the calls actually did."
            // ⛔ "2 things Alfred could not finish on its own" IS AN INSTRUCTION TO A PERSON,
            // and the demo seeds the calls this panel derives itself from — so on a seeded
            // board it was telling a dispatcher to go and chase calls that were never placed.
            : `${queue.length === 1 ? "One thing" : `${queue.length} things`} Alfred could not finish on its own.`
              + (queue.filter((x) => x.seeded).length
                ? ` ${queue.filter((x) => x.seeded).length === queue.length ? "Every one of them is" : `${queue.filter((x) => x.seeded).length} of them are`} derived from calls seeded for a demonstration — nobody was rung.`
                : "")} />
        {shown.length === 0
          ? <div style={{ padding: "26px 20px 30px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13.5, color: T.ink3 }}>
              {/*
                ⛔ THIS SAID "every call either connected or has not been tried", WHICH IS NOT
                WHAT AN EMPTY QUEUE MEANS. Two no-answers to one driver is below the threshold
                — tried, nobody answered. A call still sitting at `placed` because nobody
                refreshed it has an unknown outcome. A failed row with no reason recorded is
                dropped on the way in. And only the newest 50 calls are read at all. The
                panel knows about two specific things; it should claim those two and no more.
              */}
              Nothing waiting — no refused calls, and no driver with {UNANSWERED_BEFORE_ESCALATING}
              {" "}or more missed calls since they last answered.
            </div>
          : shown.map((q) => <QueueItem key={q.id} q={q} drivers={drivers} loads={loads} seat={seat} onOpenLoad={onOpenLoad} onResolve={onResolve} />)}
      </Panel>

      <Panel>
        <PanelHead title="Call volume" note="Today, 6am to 6pm. Driver reachability lives on the Drivers tab." />
        <div style={{ padding: "18px 20px 18px" }}>
          {callsToday === 0 ? (
            <div style={{ fontSize: 13.5, color: T.ink3 }}>
              {seededCount
                ? `No calls have been placed. The ${seededCount} call${seededCount === 1 ? "" : "s"} on this board ${seededCount === 1 ? "was" : "were"} seeded for a demonstration, so there is nothing to measure here.`
                : "No calls today yet. This fills in as Alfred places them."}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 128 }}>
                {hourly.bins.map((v, i) => <span key={i} title={`${HOURLY_LABELS[i]} · ${v}`} style={{ flex: 1, height: `${(v / maxHour) * 100}%`, minHeight: v > 0 ? 2 : 0, background: v === maxHour && v > 0 ? T.ink : "#D2D2D2", borderRadius: 2 }} />)}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {HOURLY_LABELS.map((l, i) => <span key={i} style={{ flex: 1, textAlign: "center", fontFamily: mono, fontSize: 10, color: T.ink4 }}>{l}</span>)}
              </div>
              {/* ⛔ "Voicemails left 18", "Calls avoided by API push 31" and "Median handoff
                  pickup 9m" were typed in. Nothing counts a voicemail on a freight leg,
                  nothing records a call an API push replaced, and there is no handoff
                  model at all. Only the two figures the call rows can actually support. */}
              <div style={{ borderTop: `1px solid ${T.hairSoft}`, marginTop: 18, paddingTop: 16, display: "flex", flexWrap: "wrap", gap: 30 }}>
                {[["Busiest hour", peak], ["Answered today", `${answeredToday} of ${callsToday}`]].concat(hourly.outside ? [["Outside 6a-6p", String(hourly.outside)]] : []).map(([k, v]) => (
                  <span key={k} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <Micro>{k}</Micro>
                    <span style={{ fontFamily: mono, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>{v}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}

/* ------------------------------- call panel -------------------------------- */

/**
 * What is happening with the call Alfred was asked to place.
 *
 * ⛔ IT USED TO STREAM A SCRIPT. `call.lines` filled on a timer from a pre-written array and
 * scrolled like a live transcript. There is no live transcript: nothing pushes turn-by-turn
 * audio into this app, and pretending otherwise meant a dispatcher watched a conversation
 * that was not occurring. The transcript arrives in one piece when the call ends.
 *
 * Five honest states: asking the platform, the platform has it, it ended, it was refused,
 * or we would not place it (no consent, no driver, a broker leg with no dial path).
 */
export function CallPanel({ call, onEnd }) {
  const mm = String(Math.floor(call.elapsed / 60)).padStart(2, "0");
  const ss = String(call.elapsed % 60).padStart(2, "0");
  const live = call.phase === "placing" || call.phase === "placed";
  const bad = call.phase === "failed" || call.phase === "blocked";
  // ⛔ HUNG UP IS NOT FINISHED. The platform reports the hang-up before the transcript is
  // written, and the backend holds an answered call open for it. This said "Alfred is calling"
  // with the clock running the whole time, to a room that had just watched the call end.
  const wrapping = call.phase === "placed" && call.hungUp;
  const head = call.phase === "placing" ? `Asking the platform to ring ${call.partyName}…`
    : wrapping ? `Call with ${call.partyName} ended — writing up the transcript…`
    : call.phase === "placed" && call.doubt ? `No word yet from the platform on the call to ${call.partyName}. It may have gone through.`
    : call.phase === "placed" ? `Alfred is calling ${call.partyName}`
      : call.phase === "ended" ? `Call with ${call.partyName} ended`
        : call.phase === "failed" ? `Could not call ${call.partyName}`
          : `Not calling ${call.partyName}`;
  const turns = call.result && call.result.transcript ? call.result.transcript : [];

  return (
    <div style={{ margin: "0 20px 16px", border: `1px solid ${bad ? T.hair : T.ink}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: bad ? T.wash : T.ink, color: bad ? T.ink : "#fff", flexWrap: "wrap" }}>
        {live && <Waveform active={call.phase === "placed" && !wrapping} invert />}
        <span style={{ fontSize: 13, fontWeight: 500 }}>{head}</span>
        {live && <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, opacity: 0.75 }}>{mm}:{ss}</span>}
        {call.result && call.result.outcome && (
          <span style={{ marginLeft: live ? 0 : "auto", fontFamily: mono, fontSize: 12, opacity: 0.8 }}>{call.result.outcome}</span>
        )}
        <button onClick={onEnd} style={{ font: "inherit", fontSize: 12, color: bad ? T.ink : "#fff", background: "none", border: `1px solid ${bad ? T.hair : "rgba(255,255,255,.4)"}`, borderRadius: 5, padding: "2px 8px", cursor: "pointer" }}>
          {live ? "Stop watching" : "Close"}
        </button>
      </div>

      {call.error && (
        <div style={{ padding: "11px 13px", fontSize: 13, color: T.ink2, lineHeight: 1.5, borderTop: `1px solid ${T.hairSoft}` }}>{call.error}</div>
      )}

      {/* An AI persona's call is heard live, here, through this browser (LiveListen.jsx). */}
      {call.persona && live && (
        <div style={{ padding: "11px 13px", borderTop: `1px solid ${T.hairSoft}` }}>
          <LiveListen T={T} mono={mono} callId={call.callId} name={call.partyName} listen={freight.listenCall} />
        </div>
      )}

      {call.phase === "placed" && (
        <div style={{ padding: "11px 13px", fontSize: 12.5, color: T.ink3, lineHeight: 1.5, borderTop: `1px solid ${T.hairSoft}` }}>
          {wrapping
            ? "The call has ended. The transcript usually lands within a minute, and appears here."
            : call.persona ? "The transcript and what Alfred captured land here when the call ends."
              : "The platform has the call. There is no live transcript — this updates when the call ends."}
        </div>
      )}

      {call.phase === "ended" && (
        <div style={{ borderTop: `1px solid ${T.hairSoft}`, padding: 13 }}>
          {turns.length === 0 ? (
            <div style={{ fontSize: 13, color: T.ink3 }}>
              {call.result && call.result.status === "no_answer" ? "No answer — the platform recorded no transcript." : "The call ended with no transcript."}
            </div>
          ) : (
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 9 }}>
              {turns.map((l, i) => (
                <div key={i} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", color: l.who === "alfred" ? T.ink : T.ink4, minWidth: 42, paddingTop: 3 }}>{l.speaker || l.who || ""}</span>
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: l.who === "alfred" ? T.ink : T.ink2 }}>{l.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- call plan -------------------------------- */

function StepMarker({ state }) {
  if (state === "done") return <span style={{ width: 15, height: 15, borderRadius: 999, background: T.ink, color: "#fff", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</span>;
  if (state === "due") return <span style={{ width: 15, height: 15, borderRadius: 999, border: `2px solid ${T.ink}`, background: "#fff", flexShrink: 0, boxSizing: "border-box" }} />;
  return <span style={{ width: 15, height: 15, borderRadius: 999, border: `1px solid ${T.hair}`, background: "#fff", flexShrink: 0, boxSizing: "border-box" }} />;
}

function CallPlan({ load, driver, onCallStep, onPatchStep, onAddStep, busy }) {
  const due = load.plan.find((s) => s.state === "due");
  const [openId, setOpenId] = useState(due ? due.id : null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", at: "" });
  const done = load.plan.filter((s) => s.state === "done").length;
  const auto = load.broker.updateVia !== "call";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <Micro>{done} of {load.plan.length} steps done · {load.stops.length} stops</Micro>
        <button onClick={() => setAdding((a) => !a)} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>{adding ? "Cancel" : "Add a call"}</button>
      </div>

      {adding && (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 12, marginBottom: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <input placeholder="What should Alfred say? e.g. broker wants the load bars checked" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            style={{ font: "inherit", fontSize: 13.5, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "8px 10px", outline: "none" }} />
          <div style={{ display: "flex", gap: 8 }}>
            <input placeholder="Time" value={draft.at} onChange={(e) => setDraft({ ...draft, at: e.target.value })}
              style={{ font: "inherit", fontFamily: mono, fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "8px 10px", outline: "none", width: 130 }} />
            <Button size="sm" disabled={!draft.title.trim()} onClick={() => { onAddStep(load.id, draft); setDraft({ title: "", at: "" }); setAdding(false); }}>Add to plan</Button>
          </div>
        </div>
      )}

      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 7, top: 8, bottom: 8, width: 1, background: T.hairSoft }} />
        {load.plan.map((s) => {
          const open = openId === s.id;
          const pushed = s.party === "broker" && auto;
          const quiet = s.party === "driver" && inQuietHours(s.at, driver);
          const blocked = s.party === "driver" && inMotion(driver);
          const local = s.party === "driver" ? localTimeFor(s.at, driver) : null;
          const stop = s.stopSeq ? load.stops.find((x) => x.seq === s.stopSeq) : null;
          return (
            <div key={s.id} style={{ position: "relative", paddingLeft: 26, paddingBottom: 4 }}>
              <span style={{ position: "absolute", left: 0, top: 10 }}><StepMarker state={s.state} /></span>
              <button onClick={() => setOpenId(open ? null : s.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: "8px 6px 8px 0", cursor: "pointer", textAlign: "left", font: "inherit", borderRadius: 6 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13.5, fontWeight: s.state === "due" ? 600 : 500, color: s.state === "scheduled" ? T.ink2 : T.ink, letterSpacing: -0.2 }}>{s.title}</span>
                    <Soft>{s.party}</Soft>
                    {s.extra && <Tag>extra call</Tag>}
                    {pushed && <Soft>{UPDATE_VIA[load.broker.updateVia]}</Soft>}
                    {quiet && s.state !== "done" && <Tag>quiet hours</Tag>}
                  </span>
                </span>
                <span style={{ fontFamily: mono, fontSize: 11.5, color: s.state === "due" ? T.ink : T.ink3 }}>{s.at || "—"}</span>
              </button>

              {open && (
                <div className="alf-fade" style={{ padding: "4px 0 14px" }}>
                  <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{s.detail}</div>
                  {stop && <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{stop.city} · appointment {stop.window}</div>}
                  {s.captures.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                      {s.captures.map((c) => <span key={c} style={{ fontSize: 11.5, border: `1px solid ${T.hair}`, borderRadius: 4, padding: "2px 7px", color: T.ink2 }}>{c}</span>)}
                    </div>
                  )}
                  {local && <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{s.at} office time is {local} for {driver.name.split(" ")[0]}.</div>}
                  {pushed && <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{load.broker.name} takes updates through {UPDATE_VIA[load.broker.updateVia]}. Alfred pushes this instead of calling {load.broker.contact}.</div>}

                  <div style={{ marginTop: 10 }}>
                    <Micro style={{ marginBottom: 5 }}>Notes for this call</Micro>
                    <textarea value={s.notes} placeholder="Anything Alfred should say, ask, or pass along." onChange={(e) => onPatchStep(load.id, s.id, { notes: e.target.value })} rows={2}
                      style={{ width: "100%", font: "inherit", fontSize: 13, color: T.ink, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 7, padding: "8px 10px", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.45 }} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <input value={s.at} placeholder="Time" onChange={(e) => onPatchStep(load.id, s.id, { at: e.target.value })}
                      style={{ font: "inherit", fontFamily: mono, fontSize: 12.5, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "6px 9px", outline: "none", width: 110 }} />
                    {s.state !== "done" && (
                      <>
                        <Button size="sm" disabled={busy || (blocked && !pushed)} onClick={() => onCallStep(load.id, s.id)} title={blocked && !pushed ? "Driver is in motion" : undefined}>
                          {pushed ? "Push update" : "Call now"}
                        </Button>
                        <Button size="sm" kind="quiet" onClick={() => onPatchStep(load.id, s.id, { state: s.state === "due" ? "scheduled" : "due" })}>{s.state === "due" ? "Hold" : "Mark due"}</Button>
                      </>
                    )}
                    {s.state === "done" && <span style={{ fontSize: 12.5, color: T.ink3 }}>Completed at {s.at}</span>}
                  </div>

                  {blocked && !pushed && s.state !== "done" && (
                    <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9, lineHeight: 1.5 }}>
                      {driver.name.split(" ")[0]} is driving{driver.eld && driver.eld.speed != null ? ` at ${driver.eld.speed} mph` : ""}. Alfred holds this call and places it when the ELD shows stopped.
                    </div>
                  )}
                  {quiet && s.state !== "done" && !blocked && (
                    <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9, lineHeight: 1.5 }}>
                      That lands inside {driver.name.split(" ")[0]}'s quiet hours ({driver.quiet} {driver.tz}). Alfred waits for the window to open unless you override it.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------- inspector -------------------------------- */

// Exported for Dispatch.render.test.mjs. An independent review proved this component
// throws on a load with no driver — loads.driver_id is nullable and the demo seed writes
// one deliberately — and it had no render coverage at all.
export function Inspector({ load, driver, drivers, calls, call, onCallStep, onEndCall, onPatchStep, onAddStep, onSetBrokerPref, onRelay, onClose }) {
  const dStats = callStats(calls, driver && driver.id);
  const [relayOpen, setRelayOpen] = useState(false);
  const [relayTo, setRelayTo] = useState("");

  if (!load) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 10, padding: 32, textAlign: "center" }}>
        <Waveform active={false} />
        <div style={{ fontSize: 14, color: T.ink2, marginTop: 4 }}>Select a load</div>
        <div style={{ fontSize: 12.5, color: T.ink3, maxWidth: 210, lineHeight: 1.5 }}>The call plan, stops, paperwork, and contacts open here.</div>
      </div>
    );
  }
  const activeCall = call && call.loadId === load.id ? call : null;
  const c1 = staffById(load.contact1), c2 = staffById(load.contact2);
  const route = routeContact(load);
  const nextDue = load.plan.find((s) => s.state === "due");
  const det = openDetention(load);
  const late = lateRisk(load);
  const pressure = clockPressure(load, driver);
  const pushed = nextDue && nextDue.party === "broker" && load.broker.updateVia !== "call";
  const blocked = nextDue && nextDue.party === "driver" && inMotion(driver);

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ padding: "18px 20px 15px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, letterSpacing: 0.4 }}>{load.ref}</span>
          <button onClick={onClose} className="alf-close" style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Close</button>
        </div>
        <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: -0.6, marginTop: 6, lineHeight: 1.25 }}>{laneLabel(load)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 10, flexWrap: "wrap" }}>
          <Chip status={activeCall ? "calling" : load.status} />
          {load.rate && <span style={{ fontFamily: mono, fontSize: 12.5 }}>{load.rate}</span>}
          <span style={{ fontSize: 11.5, color: T.ink3 }}>{driver ? [driver.name, driver.truck].filter(Boolean).join(" · ") : "No driver assigned"}</span>
        </div>
        <div style={{ marginTop: 10 }}><EldLine driver={driver} /></div>

        {late && (
          <div style={{ marginTop: 14, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Tag solid>late risk</Tag>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{waitLabel(late.minutes)} past the {late.stop.name} window</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
              ETA {late.stop.etaAt} against a {late.stop.windowEnd} close. Alfred won't agree to a new appointment — it hands this to {route.person ? route.person.name : "the load contact"} while there's still time to move it.
            </div>
          </div>
        )}

        {pressure && (
          <div style={{ marginTop: 10, padding: "11px 13px", border: `1px solid ${T.hair}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Soft>clock pressure</Soft>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{pressure.have}h drive left against {pressure.need}h to the next stop</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 5, lineHeight: 1.5 }}>
              Alfred reports the hours. It never tells a driver whether they can run it — that decision is the office's.
            </div>
          </div>
        )}

        {!activeCall && nextDue && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <Micro>Next step</Micro>
              <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 3 }}>{nextDue.title}</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>
                {blocked && !pushed ? `Holding — ${(driver ? driver.name : "the driver").split(" ")[0]} is in motion`
                  : pushed ? `${UPDATE_VIA[load.broker.updateVia] || "The broker"} · no call needed`
                  : `${nextDue.party === "driver" ? (driver ? driver.name : "No driver assigned") : (load.broker.contact || "the broker")} · ${nextDue.at || "unscheduled"}`}
              </div>
            </div>
            <Button size="sm" disabled={blocked && !pushed} onClick={() => onCallStep(load.id, nextDue.id)}>{pushed ? "Push update" : "Call now"}</Button>
          </div>
        )}
      </div>

      {activeCall && <CallPanel call={activeCall} onEnd={onEndCall} />}
      {det && <div style={{ padding: "0 20px 16px" }}><DetentionClock stop={det} broker={load.broker} /></div>}

      {/* ⛔ PROGRESS IS NOT PERSISTED. Marking a step done lives in React state; nothing
          POSTs it, so it is gone on reload. The code comments were honest about this and the
          SCREEN was not — it rendered "Completed at 3:42 PM" like a record. Said out loud
          here until there is a write path. */}
      {/* "· not saved" was true until persistPlan existed. It writes to detail.planOver on
          every change now, so the label said the opposite of what the code does. */}
      <Disclosure title="Call plan" meta={`${load.plan.filter((s) => s.state === "done").length}/${load.plan.length} done`} defaultOpen>
        <CallPlan load={load} driver={driver} onCallStep={onCallStep} onPatchStep={onPatchStep} onAddStep={onAddStep} busy={!!call} />
      </Disclosure>

      <Disclosure title="Stops" meta={`${load.stops.length} stops`}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 5, top: 10, bottom: 10, width: 1, background: T.hairSoft }} />
          {load.stops.map((s) => (
            <div key={s.seq} style={{ position: "relative", paddingLeft: 22, paddingBottom: 14 }}>
              <span style={{ position: "absolute", left: 0, top: 5, width: 11, height: 11, borderRadius: s.type === "pickup" ? 2 : 999, boxSizing: "border-box",
                background: s.state === "complete" ? T.ink : "#fff", border: `${s.state === "arrived" ? 2 : 1}px solid ${s.state === "canceled" ? T.hair : T.ink}` }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.name}</span>
                <Soft>{s.type}</Soft>
                {s.state === "arrived" && <Tag solid>on site</Tag>}
                {late && late.stop.seq === s.seq && <Tag>late risk</Tag>}
              </div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                {s.city} · {s.window}{s.etaAt && s.state !== "complete" ? ` · ETA ${s.etaAt}` : ""}
              </div>
            </div>
          ))}
        </div>
      </Disclosure>

      <Disclosure title="Paperwork" meta={`${load.paperwork.filter((d) => d.v).length}/${load.paperwork.length}`}>
        <Paperwork load={load} />
      </Disclosure>

      <Disclosure title="Company contacts" meta={route.person ? route.person.name : "Not set"}>
        {c1 && (<div><Micro>Primary</Micro><Field label={c1.name} value={c1.role} /><Field label="Phone" value={c1.phone} isMono /><Field label="Available" value={c1.hours} /></div>)}
        {c2 && (<div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.hairSoft}` }}><Micro>Second contact</Micro><Field label={c2.name} value={c2.role} /><Field label="Phone" value={c2.phone} isMono /><Field label="Available" value={c2.hours} /></div>)}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
          {route.person ? (route.fallback ? `Right now Alfred would reach ${route.person.name} — ${c1 ? c1.name.split(" ")[0] : "the primary"} is off shift.` : `Right now Alfred would reach ${route.person.name}.`) : "No one is set to take a handoff on this load."}
        </div>
      </Disclosure>

      {/* ⛔ EVERY FIELD HERE IS JOINED, NOT INTERPOLATED. `${driver.truck} · ${driver.trailer}`
          renders the word "null" for a driver imported from a CSV with just a name and a
          number — which is the commonest shape there is. A template literal stringifies null;
          JSX children drop it. */}
      <Disclosure title="Driver" meta={driver ? driver.name : "No driver assigned"}>
        {!driver ? (
          <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
            This load has no driver on it, so there is nobody for Alfred to call. Assign one
            from the roster and the call plan starts.
          </div>
        ) : (<>
        <Field label="Phone" value={driver.phone || "Not on file"} isMono />
        <Field label="Truck / trailer" value={truckLine(driver)} />
        <Field label="Language" value={driver.language || "Not recorded"} />
        <Field label="Time zone" value={zoneLine(driver)} />
        <Field label="Answered" value={dStats.settled ? `${pct(dStats.rate)} of ${dStats.settled}` : "No calls yet"} isMono />
        <Field label="ELD" value={driver.eld ? `${driver.eld.provider} · ${driver.eld.dutyLeft}h duty left` : "Not connected"} />
        {driver.note && <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.hairSoft}` }}>{driver.note}</div>}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}` }}>
          {!relayOpen ? (
            <button onClick={() => setRelayOpen(true)} style={{ font: "inherit", fontSize: 13, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}>Hand the rest of this load to another driver</button>
          ) : (
            <div className="alf-fade">
              <Micro style={{ marginBottom: 6 }}>Relay to</Micro>
              <select value={relayTo} onChange={(e) => setRelayTo(e.target.value)} style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}>
                <option value="">Pick a driver</option>
                {drivers.filter((d) => d.id !== driver.id).map((d) => <option key={d.id} value={d.id}>{d.name} — {d.truck}{d.eld && d.eld.driveLeft != null ? ` · ${d.eld.driveLeft}h left` : ""}</option>)}
              </select>
              <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8 }}>
                Remaining steps move to the new driver, who gets a fresh assignment call. Completed steps and paperwork stay on the load.
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <Button size="sm" disabled={!relayTo} onClick={() => { onRelay(load.id, relayTo); setRelayOpen(false); setRelayTo(""); }}>Hand it over</Button>
                <Button size="sm" kind="quiet" onClick={() => setRelayOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
        </>)}
      </Disclosure>

      {/* `broker` is a jsonb that can be `{}` — every read here is defaulted rather than
          rendering a blank row or a dangling separator. */}
      <Disclosure title="Broker" meta={load.broker.name || "Not recorded"}>
        <Field label="Contact" value={load.broker.contact || "Not recorded"} />
        <Field label="Phone" value={load.broker.phone || "Not recorded"} isMono />
        <div style={{ marginTop: 13, paddingTop: 13, borderTop: `1px solid ${T.hairSoft}` }}>
          <Micro style={{ marginBottom: 6 }}>How they want updates</Micro>
          <select value={load.broker.updateVia} onChange={(e) => onSetBrokerPref(load.id, e.target.value)} style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}>
            {Object.entries(UPDATE_VIA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8 }}>
            {load.broker.updateVia === "call"
              ? `Alfred calls ${load.broker.contact || "the broker"} at each milestone${load.broker.cadence ? ` (${String(load.broker.cadence).toLowerCase()})` : ""}.`
              : load.broker.updateVia === "portal"
              ? "Portal check calls can't be automated yet. Broker steps escalate to the office instead of dialing."
              : `Milestones push to ${UPDATE_VIA[load.broker.updateVia]}. Alfred calls only if the push fails or something needs a human answer.`}
          </div>
        </div>
      </Disclosure>

      <Disclosure title="Activity" meta={load.activity.length ? String(load.activity.length) : "None"}>
        {load.activity.length === 0 ? <div style={{ fontSize: 13, color: T.ink3 }}>Nothing yet.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {[...load.activity].reverse().map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12 }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 56, paddingTop: 2 }}>{a.t}</span>
                <div><div style={{ fontSize: 13, fontWeight: 500 }}>{a.head}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 2 }}>{a.body}</div></div>
              </div>
            ))}
          </div>
        )}
      </Disclosure>
      <div style={{ height: 20 }} />
    </div>
  );
}

/* --------------------------------- settings -------------------------------- */

function IntegrationRow({ item, onDisconnect }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [testing, setTesting] = useState(false);
  const connected = item.state === "connected";
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 170 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{item.name}</span>
            <Soft>{item.kind}</Soft>
            {connected && <Tag solid>connected</Tag>}
          </span>
          <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>
            {connected ? `${item.pulls} · last sync ${item.lastSync}` : item.pulls}
          </span>
        </span>
        {connected ? (
          <>
            <span style={{ fontFamily: mono, fontSize: 12, color: T.ink2 }}>{item.keyMask}</span>
            <Button size="sm" kind="quiet" onClick={() => onDisconnect(item.id)}>Disconnect</Button>
          </>
        ) : <Button size="sm" kind="quiet" onClick={() => setOpen((o) => !o)}>{open ? "Cancel" : "Connect"}</Button>}
      </div>
      {open && !connected && (
        <div className="alf-fade" style={{ padding: "0 20px 18px" }}>
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Micro style={{ marginBottom: 5 }}>API key — not yet connected, do not paste a live key</Micro>
              <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="Integration not built yet" disabled
                style={{ width: "100%", font: "inherit", fontFamily: mono, fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box" }} />
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 6, lineHeight: 1.45 }}>{item.where}</div>
            </div>
            <div>
              <Micro style={{ marginBottom: 6 }}>What Alfred may do</Micro>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Read only", "Read and write back"].map((m) => (
                  <span key={m} style={{ fontSize: 12.5, padding: "5px 10px", borderRadius: 6, border: `1px solid ${m === "Read only" ? T.ink : T.hair}`, background: m === "Read only" ? T.ink : "#fff", color: m === "Read only" ? "#fff" : T.ink2 }}>{m}</span>
                ))}
              </div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
                Start read-only. Write-back posts call outcomes, ETAs, POD details, and empty status into {item.name} — turn it on once you trust what Alfred is capturing.
              </div>
            </div>
            {/* ⛔ "Test and connect" RAN A 1,000ms TIMER AND THEN SAID CONNECTED.
                Nothing was tested and nothing was sent — and the hint underneath read "Any
                value works in this prototype", directly below a field asking for a live
                McLeod or Motive credential. The button is gone until there is something
                behind it; a disabled control that explains itself cannot mislead. */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Button size="sm" disabled>Not available yet</Button>
              <span style={{ fontSize: 12, color: T.ink3 }}>
                No integration code exists for {item.name} yet, so there is nowhere to send a key.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Settings() {
  const [tab, setTab] = useState("integrations");
  const [flags, setFlags] = useState(TOGGLEABLE);
  const [items, setItems] = useState(INTEGRATIONS);
  /**
   * ⛔ "CONNECTING" AN INTEGRATION DOES NOTHING, AND NOW SAYS SO.
   *
   * This used to flip the row to `connected`, stamp "last sync just now" and show a masked
   * key built from the last four characters of whatever was typed — including when nothing
   * was typed, in which case it displayed `••••8f2c`, a mask of no key at all. Nothing was
   * sent anywhere and nothing was stored; the row reverted on reload.
   *
   * A carrier would hand over a live McLeod or Motive API key to a form that drops it. That
   * is the part that matters: it is not just a cosmetic fib, it invites a real credential.
   * So the key never leaves the field, and the row says what is actually true.
   */
  const disconnect = (id) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, state: "off", lastSync: null } : x)));
  const groups = [["TMS", "Dispatch software"], ["ELD", "Hours and location"], ["Visibility", "Broker tracking"], ["CRM", "Sales and customers"]];
  const connected = items.filter((i) => i.state === "connected").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 760 }}>
      <Segmented value={tab} onChange={setTab} options={[{ v: "integrations", label: "Integrations" }, { v: "calling", label: "Calling" }, { v: "limits", label: "Limits" }]} />

      {tab === "integrations" && (
        <>
          <Panel>
            <PanelHead title="Connected systems" note={`${connected} connected. With a TMS, load numbers resolve on their own. Without one, everything still works from the ratecon.`} />
            {groups.map(([kind, label]) => {
              const rows = items.filter((i) => i.kind === kind);
              if (!rows.length) return null;
              return (
                <div key={kind}>
                  <div style={{ padding: "12px 20px 6px", borderTop: `1px solid ${T.hairSoft}` }}><Micro>{label}</Micro></div>
                  {rows.map((i) => <IntegrationRow key={i.id} item={i} onDisconnect={disconnect} />)}
                </div>
              );
            })}
            <div style={{ padding: "14px 20px 18px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink2, lineHeight: 1.55 }}>
              A stale ELD key is worse than no key — Alfred's don't-call-in-motion rule depends on it. If a sync goes quiet, that shows up on Overview, not just here.
            </div>
          </Panel>
          <Panel>
            <PanelHead title="If nothing is connected" note="What Alfred falls back to, in order." />
            {[["Rate confirmation upload", "Full load and a stop-aware call plan built from the document. This is the default and it's fine."],
              ["Load number lookup", "Needs a TMS. Without one this path is hidden."],
              ["Manual entry", "Always available. Load number, two cities, and a contact is the minimum."]].map(([t, d], i) => (
              <div key={t} style={{ display: "flex", gap: 12, padding: "13px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
                <div><div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{t}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{d}</div></div>
              </div>
            ))}
          </Panel>
        </>
      )}

      {tab === "calling" && (
        <Panel>
          <PanelHead title="How Alfred calls" note="Voice only for now. Alfred doesn't text, and it doesn't handle images — paperwork details are read back and confirmed on the call." />
          {flags.map((f) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{f.label}</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{f.note}</div>
              </div>
              <Toggle on={f.on} onChange={(v) => setFlags((xs) => xs.map((x) => (x.id === f.id ? { ...x, on: v } : x)))} />
            </div>
          ))}
          <div style={{ padding: "13px 20px 16px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12, color: T.ink3, lineHeight: 1.55 }}>
            The recording disclosure plays on every call regardless of these settings. Drivers acknowledge it at onboarding and can withdraw consent by telling Alfred, which routes their calls to a person.
          </div>
        </Panel>
      )}

      {tab === "limits" && (
        <Panel>
          <PanelHead title="What Alfred will never do" note="Fixed. Not switchable from this screen, by a driver on a call, or by a broker asking nicely. Hand this list to a safety director or an insurer." />
          {LIMITS.map((l, i) => (
            <div key={l.t} style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, display: "flex", gap: 12 }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
              <div><div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{l.t}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{l.d}</div></div>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/* --------------------------------- intake ---------------------------------- */

const EMPTY_FORM = {
  ref: "", rate: "", commodity: "", puName: "", puCity: "", puWindow: "",
  doName: "", doCity: "", doWindow: "", brokerName: "", brokerContact: "", brokerPhone: "",
  // ⛔ EMPTY, NOT SEED IDS. These were "d1" and "s1", left from the days of seed data. The
  // backend does `Number(l.driverId)` on whatever arrives, node-postgres serialises the
  // resulting NaN as the STRING "NaN", and Postgres rejects that for an integer comparison —
  // so the default form 500'd with "Internal error" on a screen whose own heading says "this
  // one saves". The driver <select> also had no option matching "d1", so it rendered blank
  // while holding a value, and canSave was satisfied by a contact nobody had picked.
  updateVia: "call", driverId: "", contact1: "", contact2: "", secondOpen: false,
};

function ChoiceRow({ title, note, tag, onClick }) {
  return (
    <button onClick={onClick} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, textAlign: "left", font: "inherit", padding: "16px 18px", background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 9, cursor: "pointer" }}>
      <span style={{ flex: 1 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.25 }}>{title}</span>
          {tag && <Tag solid>{tag}</Tag>}
        </span>
        <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>{note}</span>
      </span>
      <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
    </button>
  );
}

function VField({ label, value, onChange, placeholder, source, flag, isMono, half }) {
  return (
    <div style={{ flex: half ? "1 1 46%" : "1 1 100%", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
        <Micro>{label}</Micro>
        {source && <span style={{ fontFamily: mono, fontSize: 9.5, color: T.ink4 }}>{source}</span>}
        {flag && <Tag>check</Tag>}
      </div>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", font: "inherit", fontSize: 14, color: T.ink, background: "#fff", border: `1px solid ${flag ? T.ink : T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box", fontFamily: isMono ? mono : sans }} />
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div>
      <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: -0.25, marginBottom: note ? 2 : 10 }}>{title}</div>
      {note && <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 10, lineHeight: 1.45 }}>{note}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{children}</div>
    </div>
  );
}

function ContactPicker({ value, onChange, label, allowNone }) {
  const s = staffById(value);
  return (
    <div style={{ flex: "1 1 100%" }}>
      <Micro style={{ marginBottom: 5 }}>{label}</Micro>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...selectStyle, width: "100%", fontSize: 14, padding: "9px 11px", boxSizing: "border-box" }}>
        {allowNone && <option value="">None</option>}
        {/* ⛔ AN EMPTY REQUIRED PICKER MUST SAY SO. With no option for "", the browser drew
            the first contact as if chosen while the form held nothing, so Save stayed grey
            until the value changed — and with ONE dispatcher on file it never could. */}
        {!allowNone && !value && <option value="" disabled>Choose a contact</option>}
        {/* ⛔ WAS `staff.map(...)` — there is no module-level `staff`; the only one is state
            inside AlfredDispatch. A ReferenceError the moment the New Load sheet reached
            "Company contact", and invisible to `vite build`, which does not resolve free
            identifiers. STAFF_LIVE is the registry the fetch publishes into. */}
        {staffList().map((p) => <option key={p.id} value={p.id}>{p.name} — {p.role}</option>)}
      </select>
      {s && (
        <div style={{ display: "flex", gap: 14, marginTop: 7, flexWrap: "wrap" }}>
          <span style={{ fontFamily: mono, fontSize: 12, color: T.ink2 }}>{s.phone}</span>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Available {s.hours}</span>
          {!s.onHours && <Soft>off shift now</Soft>}
        </div>
      )}
    </div>
  );
}

function NewLoadSheet({ onClose, onCreate, drivers }) {
  const [step, setStep] = useState("choose");
  const [lookupRef, setLookupRef] = useState("");
  const [fileName, setFileName] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [extracted, setExtracted] = useState({});
  const fileRef = useRef(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  /**
   * ⛔ THE DOCUMENT WAS NEVER READ.
   *
   * This played a four-line progress animation — "Reading the document", "Pulling stops and
   * appointments" — for two seconds, then filled the form from `RATECON_EXTRACT`: a
   * hardcoded load, RL-99231, Silver Eagle Distributors, $2,275, with per-field page
   * citations ("p.1", "p.2") claiming where in the PDF each value came from. Whatever file
   * the dispatcher picked, they got that load, and the header told them "Filled in from the
   * rate confirmation. Everything came through clean."
   *
   * There is no document parser anywhere in this app. So the upload now hands over to
   * manual entry and says why, which is what actually happens.
   */
  useEffect(() => {
    if (step !== "reading") return;
    const t = setTimeout(() => {
      setExtracted({});
      setStep("verify");
    }, 450);
    return () => clearTimeout(t);
  }, [step]);

  const canSave = form.ref.trim() && form.puCity.trim() && form.doCity.trim() && form.contact1;

  return (
    <div className="alf-fade" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5vh 16px 16px", zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} className="alf-rise" style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 12, border: `1px solid ${T.ink}`, maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 22px 14px", borderBottom: step === "choose" ? "none" : `1px solid ${T.hairSoft}` }}>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.4 }}>{step === "verify" ? "Check the details" : "New load"}</div>
          <div style={{ fontSize: 13, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>
            {step === "choose" && "Start with whatever you already have."}
            {step === "lookup" && "Alfred will pull the load from your system."}
            {step === "reading" && (fileName ? `Reading ${fileName}` : `Looking up ${lookupRef}`)}
            {step === "verify" && (Object.keys(extracted).length
              ? "Reading a rate confirmation is not built yet, so nothing was taken from the file. Enter the load below."
              : "Enter what you have. Load number, both cities, and a contact are required.")}
          </div>
        </div>

        <div style={{ padding: "18px 22px 20px", overflowY: "auto" }}>
          {step === "choose" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* ⛔ "Alfred fills in the load, stops, broker and rate" WAS NOT TRUE — there is
                  no document parser in this app. Whatever file was chosen, the form filled
                  with one hardcoded load (RL-99231, Silver Eagle Distributors, $2,275) and
                  the header said "Everything came through clean." */}
              <ChoiceRow title="Upload the rate confirmation" tag="Not built yet" note="Alfred cannot read a document yet. Picking a file takes you to manual entry." onClick={() => fileRef.current && fileRef.current.click()} />
              <ChoiceRow title="Look up a load number" note="Needs a connected TMS or load board. None is connected yet." onClick={() => setStep("lookup")} />
              <ChoiceRow title="Enter it by hand" note="Type in what you have. This one saves." onClick={() => setStep("verify")} />
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { const f = e.target.files && e.target.files[0]; setFileName(f ? f.name : "ratecon.pdf"); setStep("reading"); }} style={{ display: "none" }} />
            </div>
          )}

          {step === "lookup" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <VField label="Load number" value={lookupRef} onChange={(v) => setLookupRef(v.toUpperCase())} placeholder="e.g. HOU-44821" isMono />
              <div style={{ display: "flex", gap: 8 }}>
                <Button disabled={!lookupRef.trim()} onClick={() => { setFileName(""); setStep("reading"); }}>Find load</Button>
                <Button kind="quiet" onClick={() => setStep("choose")}>Back</Button>
              </div>
            </div>
          )}

          {/* ⛔ WAS A FOUR-STEP PROGRESS LIST — "Reading the document", "Pulling stops and
              appointments", "Matching the broker", "Building a stop-aware call plan" —
              ticking off on half-second timers over a file nothing opened. */}
          {step === "reading" && (
            <div style={{ padding: "6px 0 10px", fontSize: 13.5, color: T.ink3 }}>One moment…</div>
          )}

          {step === "verify" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <Section title="Load">
                <VField half label="Load number" value={form.ref} onChange={set("ref")} placeholder="Required" source={extracted.ref} isMono />
                <VField half label="Rate" value={form.rate} onChange={set("rate")} placeholder="$" source={extracted.rate} isMono />
                <VField label="Commodity and weight" value={form.commodity} onChange={set("commodity")} placeholder="Optional" source={extracted.commodity} flag={LOW_CONFIDENCE.includes("commodity") && extracted.commodity} />
              </Section>
              <Section title="Pickup" note="Multi-pick loads: save this one, then add stops from the load panel — the plan rebuilds around them.">
                <VField label="Shipper" value={form.puName} onChange={set("puName")} source={extracted.puName} />
                <VField half label="City, state" value={form.puCity} onChange={set("puCity")} placeholder="Required" source={extracted.puCity} />
                <VField half label="Window" value={form.puWindow} onChange={set("puWindow")} source={extracted.puWindow} flag={LOW_CONFIDENCE.includes("puWindow") && extracted.puWindow} />
              </Section>
              <Section title="Drop-off">
                <VField label="Consignee" value={form.doName} onChange={set("doName")} source={extracted.doName} />
                <VField half label="City, state" value={form.doCity} onChange={set("doCity")} placeholder="Required" source={extracted.doCity} />
                <VField half label="Window" value={form.doWindow} onChange={set("doWindow")} source={extracted.doWindow} />
              </Section>
              <Section title="Broker">
                <VField label="Brokerage" value={form.brokerName} onChange={set("brokerName")} source={extracted.brokerName} />
                <VField half label="Contact" value={form.brokerContact} onChange={set("brokerContact")} source={extracted.brokerContact} />
                <VField half label="Phone" value={form.brokerPhone} onChange={set("brokerPhone")} source={extracted.brokerPhone} isMono />
                <div style={{ flex: "1 1 100%" }}>
                  <Micro style={{ marginBottom: 5 }}>How they want updates</Micro>
                  <select value={form.updateVia} onChange={(e) => set("updateVia")(e.target.value)} style={{ ...selectStyle, width: "100%", fontSize: 14, padding: "9px 11px", boxSizing: "border-box" }}>
                    {Object.entries(UPDATE_VIA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </Section>
              <Section title="Driver">
                <select value={form.driverId} onChange={(e) => set("driverId")(e.target.value)} style={{ ...selectStyle, width: "100%", fontSize: 14, padding: "9px 11px", boxSizing: "border-box" }}>
                  {/* A load with no driver yet is a real state — it is what `needs_driver`
                      means, and the board renders it. Without this option the select shows
                      the first driver while the form holds "", which is a lie either way. */}
                  <option value="">No driver yet</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.truck ? ` — ${d.truck}` : ""}</option>)}
                </select>
              </Section>
              <Section title="Company contact" note="Who Alfred calls when a person has to step in on this load.">
                <ContactPicker value={form.contact1} onChange={set("contact1")} label="Primary" />
                {!form.secondOpen ? (
                  <button onClick={() => setForm((f) => ({ ...f, secondOpen: true }))} style={{ font: "inherit", fontSize: 13.5, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3, textAlign: "left" }}>Add a second contact</button>
                ) : (
                  <div className="alf-fade" style={{ flex: "1 1 100%" }}>
                    <ContactPicker value={form.contact2} onChange={set("contact2")} label="Second contact" allowNone />
                    <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8 }}>If the primary is off shift, or doesn't pick up within 20 minutes, this contact gets it.</div>
                  </div>
                )}
              </Section>
            </div>
          )}
        </div>

        {step !== "reading" && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "13px 22px", borderTop: `1px solid ${T.hairSoft}`, background: "#fff", borderRadius: "0 0 12px 12px" }}>
            <div>{step === "verify" && <Button kind="quiet" size="sm" onClick={() => { setStep("choose"); setForm(EMPTY_FORM); setExtracted({}); }}>Start over</Button>}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button kind="quiet" onClick={onClose}>Cancel</Button>
              {step === "verify" && <Button disabled={!canSave} onClick={() => canSave && onCreate(form, Object.keys(extracted).length ? "Rate confirmation" : "Manual entry")}>Save and build the call plan</Button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- drivers --------------------------------- */


/* one driver, everything about them — cues first, then loads, then their calls */
export function DriverProfile({ driver, drivers, loads, onBack, onOpenLoad, calls: allCalls }) {
  const [openCall, setOpenCall] = useState(null);
  const mine = loads.filter((l) => l.driverId === driver.id && !CLOSED.includes(l.status));
  const past = loads.filter((l) => l.driverId === driver.id && CLOSED.includes(l.status));
  // This driver's calls, out of the carrier's real history. Was CALL_LOG — seven invented
  // calls that appeared on EVERY driver's profile, including a driver imported five minutes ago.
  const calls = (allCalls || []).filter((c) => c.driverId === driver.id);
  const stats = callStats(allCalls, driver.id);
  // ⛔ TWO TOGGLES USED TO LIVE HERE AND NEITHER SAVED ANYTHING.
  //
  // "Spanish only" promised "Every call to this driver runs in Spanish end to end —
  // greeting, disclosure, questions, and read-backs. No English fallback." It wrote to React
  // state, so it was gone on refresh — and even a saved value would not have reached a call:
  // placeDriverCall sends the driver's truck, trailer, timezone and quiet hours to core, and
  // core's freight route has no language parameter at all. Three layers, none of them
  // carrying the promise.
  //
  // "Can give Alfred tasks" was worse. It toggled `canRequest`, which was derived from
  // CONSENT and is what gates the Call button — so flipping a switch about driver-initiated
  // errands flipped the consent gate for a driver who never agreed to be called. The
  // backend's second door refused the dial (403 no_consent), so nobody was rung; the screen
  // offered it anyway, which is the one thing the two-door design exists to prevent. And the
  // feature it claimed to configure — a driver asking Alfred to phone a scale or a shop —
  // does not exist in the backend in any form.
  //
  // Both are read-only statements of the record now. Consent is granted in setup, with the
  // wording the driver agreed to; that is the legally meaningful act, and a toggle is not.

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <button onClick={onBack} style={{ font: "inherit", fontSize: 13, color: T.ink2, background: "none", border: "none", cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>‹ All drivers</button>

      {/*
        ⛔ THE FIRST PANEL ON THIS SCREEN WAS A WELLBEING PANEL WITH NOTHING IN IT, EVER.

        Titled "Nothing flagged right now" and permanently so: its rows came from a seed
        array that is now empty, and there is no wellbeing concept in the backend — nothing
        on any call produces a flag. So the first thing a dispatcher saw on opening a driver
        was a heading, a "clear" chip and the sentence "No flags on file."

        The intent is good and it should come back when a call can actually raise one. Until
        then the operational picture leads, which is what there is.
      */}
      {/* 2 — who they are and how Alfred talks to them */}
      <Panel>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px 16px", flexWrap: "wrap" }}>
          <span style={{ width: 44, height: 44, borderRadius: 999, border: `1px solid ${T.hair}`, fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {driver.name.split(" ").map((n) => n[0]).join("")}
          </span>
          <span style={{ flex: 1, minWidth: 160 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.5 }}>{driver.name}</span>
              {!driver.mayBeCalled && <Soft>not cleared for calls</Soft>}
              {driver.persona && <Soft>AI persona</Soft>}
              {inMotion(driver) && <Soft>rolling</Soft>}
            </span>
            <span style={{ display: "block", fontSize: 13, color: T.ink3, marginTop: 3 }}>{driver.truck} · {driver.trailer} · {driver.tz}</span>
          </span>
          <span style={{ textAlign: "right" }}>
            <Micro>Answered</Micro>
            <div style={{ fontFamily: mono, fontSize: 22, letterSpacing: -0.8, marginTop: 4 }}>{pct(stats.rate)}</div>
          </span>
        </div>
        <div style={{ padding: "0 20px 14px" }}><AnswerBar s={stats} /></div>
        <div style={{ padding: "0 20px 16px" }}><EldLine driver={driver} /></div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>Language on file</div>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>
              {driver.language
                ? `Recorded as "${driver.language}". Change it on the driver's row in setup.`
                : "No language recorded. Add one on the driver's row in setup."}
              {" "}⚠️ The voice platform is not yet told a driver's language — it opens in English
              and follows the driver if they answer in another one. Pinning a language to a
              driver needs core's freight route to accept one, which it does not today.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>Cleared for automated calls</div>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>
              {driver.persona
                ? `${driver.name.split(" ")[0]} is an AI persona for demonstrations: a 555-01xx number no phone has. Call rings an AI on the voice platform, never a person, and you hear the call live here.`
                : driver.mayBeCalled
                ? `Yes${driver.consentAt ? ` — recorded ${new Date(driver.consentAt).toLocaleDateString()}` : ""}. ${driver.name.split(" ")[0]} confirmed they were told calls come from an AI and are recorded.`
                : `No. Alfred will not dial ${driver.name.split(" ")[0]} until that is recorded, and the Call button stays off. It is granted on the driver's row in setup, where the wording they agreed to is stored with it.`}
            </div>
            {driver.ecName && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.hair}` }}>
                <Micro style={{ marginBottom: 6 }}>Emergency contact on file</Micro>
                <div style={{ fontSize: 12.5, color: T.ink2 }}>
                  {driver.ecName}{driver.ecPhone ? ` · ${driver.ecPhone}` : ""}
                  <span style={{ color: T.ink3 }}> — nothing calls them automatically; this is who a person rings.</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: "6px 20px 18px", borderTop: `1px solid ${T.hairSoft}` }}>
          <Field label="Phone" value={driver.phone} isMono />
          {/* Joined, not interpolated — a CSV-imported driver has neither, and
              `${null} ${null}` is the string "null null". */}
          <Field label="Quiet hours" value={zoneLine(driver)} />
          <Field label="Avg. time on the line" value={stats.avgSec == null ? "Not enough calls" : durLabel(stats.avgSec)} isMono />
          <Field label="Calls placed" value={String(stats.total)} isMono />
          <Field label="ELD" value={driver.eld ? `${driver.eld.provider} · ${driver.eld.dutyLeft}h duty left` : "Not connected"} />
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.hairSoft}` }}>{driver.note}</div>
        </div>
      </Panel>

      {/* 3 — what they're carrying right now */}
      <Panel>
        <PanelHead title="Current load" note={mine.length ? "Open it to see the call plan and stops." : "Nothing assigned right now."} />
        {mine.length === 0 && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>Available for dispatch.</div>}
        {mine.map((l) => {
          const due = l.plan.find((s) => s.state === "due");
          const late = lateRisk(l);
          const det = openDetention(l);
          return (
            <button key={l.id} onClick={() => onOpenLoad(l.ref)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "15px 20px", borderTop: `1px solid ${T.hairSoft}`, background: "none", border: "none", borderLeft: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                  <Chip status={l.status} />
                  {late && <Tag>late risk</Tag>}
                  {det && <Tag solid>detention</Tag>}
                </span>
                <span style={{ display: "block", fontSize: 15, fontWeight: 500, letterSpacing: -0.3, marginTop: 5 }}>{laneLabel(l)}</span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
                  {due ? `Next: ${due.title}${due.at ? ` at ${due.at}` : ""}` : "Plan complete"} · {l.broker.name}
                </span>
              </span>
              <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
            </button>
          );
        })}
        {past.length > 0 && (
          <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink3 }}>
            Also on file: {past.map((l) => `${l.ref} (${statusMeta(l.status).label.toLowerCase()})`).join(", ")}
          </div>
        )}
      </Panel>

      {/* 4 — every call with this driver, without leaving the profile */}
      <Panel>
        <PanelHead title={`Calls with ${driver.name.split(" ")[0]}`}
          note={calls.length
            ? `${calls.length} call${calls.length === 1 ? "" : "s"} on record${calls.filter((c) => c.seeded).length ? ` — ${calls.filter((c) => c.seeded).length} seeded for a demonstration, not placed` : ""}. The transcript appears on each one once the call ends.`
            : "No calls on record yet."} />
        {/* ⛔ "Alfred hasn't reached this driver yet" IS A CLAIM, and it is the right one only
            when the list is genuinely empty — a roster where every call is seeded has not been
            reached either, and the count above now says so rather than this line contradicting it. */}
        {calls.length === 0 && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>Alfred hasn't reached this driver yet.</div>}
        {calls.length > 0 && calls.every((c) => c.seeded) && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>Alfred has not actually reached this driver — every call below was seeded for a demonstration.</div>}
        {calls.map((c) => (
          <CallRow key={c.id} c={c} drivers={drivers} loads={loads} showDriver={false}
            open={openCall === c.id} onToggle={() => setOpenCall(openCall === c.id ? null : c.id)} />
        ))}
      </Panel>
    </div>
  );
}

// Exported for Dispatch.render.test.mjs — this tab has never had render coverage, and
// every defect it has had needed populated props.
export function Drivers({ drivers, loads, onOpenLoad, calls }) {
  const [profileId, setProfileId] = useState(null);
  // Fleet answer rate over every settled call, not an average of per-driver averages.
  const fleetStats = drivers.map((d) => callStats(calls, d.id));
  const fleetSettled = fleetStats.reduce((n, x) => n + x.settled, 0);
  const fleetAnswered = fleetStats.reduce((n, x) => n + x.answered, 0);
  const fleetFirst = fleetSettled ? fleetAnswered / fleetSettled : null;
  // Only a driver with calls on record can be below a threshold. `null < 0.7` is true in
  // JavaScript, which listed every driver who had never been called as a problem.
  const repeats = drivers.filter((d) => { const r = callStats(calls, d.id).rate; return r != null && r < 0.7; });
  const rolling = drivers.filter(inMotion).length;
  // ⛔ WAS AN AVERAGE OF `d.avgAnswerSec`, A FIELD THAT DOES NOT EXIST — NaN across a real
  // fleet. Time on the line is what the call history actually records; how long a driver
  // took to pick up is not stored anywhere.
  const talkSecs = fleetStats.map((x) => x.avgSec).filter((v) => v != null);
  const avgTalk = talkSecs.length ? Math.round(talkSecs.reduce((a, b) => a + b, 0) / talkSecs.length) : null;

  const profile = drivers.find((d) => d.id === profileId);
  if (profile) {
    return (
      <DriverProfile driver={profile} drivers={drivers} loads={loads} calls={calls}
        onBack={() => setProfileId(null)} onOpenLoad={onOpenLoad} />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <Metric label="Drivers" value={String(drivers.length)} sub={`${rolling} rolling right now`} />
          <Metric label="Answered" value={pct(fleetFirst)} sub={fleetSettled ? `Of ${fleetSettled} call${fleetSettled === 1 ? "" : "s"} that rang` : "No calls placed yet"} />
          <Metric label="Avg. time on the line" value={avgTalk == null ? "\u2014" : durLabel(avgTalk)} sub="Across calls that connected" />
          {/* "Needs a check-in" lived here, permanently 0 — see the note where its panel was. */}
          <Metric label="Cleared for calls" value={`${drivers.filter((d) => d.mayBeCalled).length}/${drivers.length}`}
            sub={drivers.every((d) => d.mayBeCalled) ? "Alfred may dial the whole roster" : "The rest cannot be dialled until consent is recorded"} emphasis />
        </div>
        {repeats.length > 0 && (
          <div style={{ borderTop: `1px solid ${T.hairSoft}`, padding: "13px 20px", fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
            {repeats.map((d) => d.name).join(", ")} {repeats.length === 1 ? "sits" : "sit"} answering fewer than 70% of the calls we place — every milestone on their loads costs extra calls.
          </div>
        )}
      </Panel>

      <Panel>
        <PanelHead title="Roster" note="Open a driver for their cues, current load, and every call with them." />
        {drivers.map((d) => {
          const assigned = loads.filter((l) => l.driverId === d.id && !CLOSED.includes(l.status));
          return (
            <button key={d.id} onClick={() => setProfileId(d.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, background: "none", border: "none", borderTop: `1px solid ${T.hairSoft}`, padding: "15px 20px", cursor: "pointer", textAlign: "left", font: "inherit" }}>
              <span style={{ width: 32, height: 32, borderRadius: 999, border: `1px solid ${T.hair}`, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {d.name.split(" ").map((n) => n[0]).join("")}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{d.name}</span>
                  {!d.mayBeCalled && <Soft>not cleared for calls</Soft>}
                  {d.persona && <Soft>AI persona</Soft>}
                  {inMotion(d) && <Soft>rolling</Soft>}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 1 }}>
                  {d.truck} · {assigned.length ? assigned.map((l) => l.ref).join(", ") : "Available"} · {d.tz}
                </span>
              </span>
              <span style={{ fontFamily: mono, fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>{pct(callStats(calls, d.id).rate)}</span>
              <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
            </button>
          );
        })}
      </Panel>

      <Panel>
        <PanelHead title="Who answers, who doesn't" note={`${repeats.length} driver${repeats.length === 1 ? "" : "s"} answering fewer than 70% of the calls we place.`} />
        <div style={{ padding: "4px 20px 6px", display: "flex", gap: 16 }}>
          <Legend fill={T.ink} label="answered" /><Legend fill="#9E9E9E" label="no answer" /><Legend fill="#DCDCDC" label="never rang" />
        </div>
        {[...drivers].map((d) => ({ d, s: callStats(calls, d.id) }))
          // Drivers with no calls sort last rather than as 0%.
          .sort((a, b) => (b.s.rate == null ? -1 : b.s.rate) - (a.s.rate == null ? -1 : a.s.rate)).map(({ d, s }) => (
          <div key={d.id} style={{ padding: "13px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{d.name}</span>
              <span style={{ fontSize: 12.5, color: T.ink3 }}>{d.truck}</span>
              <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{pct(s.rate)}</span>
            </div>
            <div style={{ marginTop: 9 }}><AnswerBar s={s} /></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, gap: 12 }}>
              <span style={{ fontSize: 12, color: T.ink3 }}>
                {s.total === 0 ? "No calls placed yet" : `${s.total} call${s.total === 1 ? "" : "s"}${s.avgSec != null ? ` · averages ${durLabel(s.avgSec)} on the line` : ""}`}
              </span>
              {s.rate != null && s.rate < 0.7 && <span style={{ fontSize: 12 }}>Needs repeat callbacks</span>}
            </div>
          </div>
        ))}
      </Panel>

      {/*
        ⛔ TWO PANELS REMOVED, BOTH PERMANENTLY EMPTY, BOTH WITH A HEADER THAT CLAIMED A
        WORKING FEATURE.

        "Check on a driver" — "When a driver says something about how they're holding up,
        Alfred flags it and tells them a person will follow up." Nothing flags anything: its
        list came from a seed array that is now empty, and there is no wellbeing concept in
        the backend. It rendered a heading, an "0 open" counter and a legal footer with
        nothing in between.

        "Calls Alfred made for drivers" — "Drivers can hand Alfred a call instead of pulling
        over to make it themselves", with a "0 today" counter and a sentence naming which
        drivers "can't" (read off the consent field). There is no errand, task or
        driver-request concept anywhere in the backend. The whole apparatus described a
        product that does not exist.

        Both come back when something produces their rows. A panel is a claim that there is
        something to show.
      */}
    </div>
  );
}

/* ----------------------------------- app ----------------------------------- */

/* ─────────────────────────────────────────────────────────────────────────────
   PROTOTYPE BANNER. Every value on this screen is seed data defined in this file
   or in the config store — the carriers, the loads, the timers, the call
   transcripts. Nothing reaches an API, no call is placed, nothing survives a
   refresh. An operator screen that looks live and isn't is the failure this
   platform keeps paying for, so it says so at the top rather than in a footnote.
   ───────────────────────────────────────────────────────────────────────────── */
/*
 * ⛔ THE "PROTOTYPE" BANNER IS GONE, AND IT WAS NOT REMOVED BY DELETING A WARNING.
 *
 * It carried three claims in turn, each true when written. The first — "Sample data, not
 * connected to live calls, and not saved" — became false the moment the Call button started
 * dialling a real driver's mobile, which is the worst direction for a safety notice to be
 * wrong in. The second told the operator whether calling was connected, read from
 * /api/health. The last caveat standing was "the call plan's progress is not saved yet".
 *
 * That is now false too: plan progress persists through persistPlan → detail.planOver, and
 * a failed save says so rather than looking saved. Every statement the banner existed to
 * make is either handled in the screen itself — the Setup banner reports whether calling is
 * live, the call panel reports what a call actually did — or no longer true.
 *
 * ⛔ WHAT IS STILL ABSENT IS SHOWN AS ABSENT, not hidden behind a label. No ELD, so the
 * telematics line says "no ELD connected". No integrations, so they read "off". No model
 * for the exceptions queue, so it is empty rather than populated with invented handoffs.
 * A banner is not what makes a screen honest; the screen is.
 */

export default function AlfredDispatch() {
  // ⛔ THE BOARD STARTS EMPTY, NOT SEEDED. It used to open on SEED_DRIVERS/SEED_LOADS —
  // invented people with invented trucks — which meant a carrier who had just imported
  // their real roster still saw Marcus Ellison and Rosa Villareal. Worse on a demo: the
  // prospect's own drivers were nowhere, and nothing on screen said why.
  //
  // Empty for the half-second the fetch takes is honest; `loaded` below is what tells the
  // difference between "still loading" and "this carrier genuinely has no drivers yet",
  // which are different things a dispatcher must not have to guess between.
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // setup | rehearsal | live | paused: what Alfred may do for this carrier right now (backend stage.mjs).
  const [stage, setStage] = useState(null);
  // The carrier's real call history, and whether the dial seam is connected at all. A
  // dispatcher reading "no calls" needs to know which of the two it means.
  const [calls, setCalls] = useState([]);
  const [callsConfigured, setCallsConfigured] = useState(true);

  // ⛔ DERIVED FROM REAL CALLS, NOT SEEDED AND NOT TYPED IN. The panel used to hold six
  // invented handoffs keyed to drivers nobody had hired. These come out of what the calls
  // actually did — a refusal the platform logged, a driver who has not picked up — so a
  // dispatcher can work from the panel instead of double-checking it.
  //
  // ⛔ AND IT STAYS BELOW `calls`. Declared above it, this useMemo reads `calls` in its
  // temporal dead zone and the whole board throws "Cannot access 'calls' before
  // initialization" on first paint — a white screen, and `vite build` was green on it.
  //
  // `resolved` is the set a person has cleared; it lives in state because clearing one is
  // a local act that does not change the call it came from.
  const [resolved, setResolved] = useState(() => new Set());
  const queue = useMemo(
    () => exceptionsFromCalls(calls).filter((e) => !resolved.has(e.id)),
    [calls, resolved],
  );
  const [refreshing, setRefreshing] = useState(false);

  // One pass, four calls — the screen needs drivers, loads and seats before it can draw a
  // row, since a load renders its driver's name and the call plan renders the on-duty seat.
  // The call history is mapped AFTER loads, because a call carries a loadId and the row
  // shows the ref a dispatcher recognises.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, l, p, c, me] = await Promise.all([
          freight.listDrivers(), freight.listLoads(), freight.listDispatchers(),
          // A carrier with no call history is normal; a seam that is not configured is
          // also normal. Neither should blank the whole board, so this one cannot reject.
          freight.listCalls().catch(() => ({ calls: [], configured: false })),
          // The carrier's stage (backend stage.mjs): only read for the notice below, so it cannot reject either.
          freight.getCarrier().catch(() => null),
        ]);
        if (!alive) return;
        setStage(me?.stage || null);
        setDrivers(driversFromApi(d));
        const mappedLoads = loadsFromApi(l);
        setLoads(mappedLoads);
        setCalls(callsFromApi(c.calls, mappedLoads));
        setCallsConfigured(c.configured);
        const people = dispatchersFromApi(p);
        setStaff(people);
        setStaffRegistry(people);   // keep the lookup and the picker on one list
        setSeat((cur) => cur || people[0]?.id || "");
        setLoadError(null);
      } catch (err) {
        if (alive) setLoadError(err?.message || "Could not load the board.");
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  /**
   * Re-read the call history.
   *
   * Nothing pushes into this app, so a call that is still running only becomes "completed
   * with a transcript" when somebody asks. `getCall` re-reads one leg from the voice
   * platform; the list endpoint alone would return the same stale row forever.
   */
  async function refreshCalls() {
    setRefreshing(true);
    try {
      const fresh = await freight.listCalls();
      // Poll the platform for the ones that have not settled, so the office sees a
      // transcript without reloading the page. A no-answer from the last 15 minutes is
      // asked again too: one settled before the transcript landed was never re-read, and an
      // answered call stayed "No answer" with nothing to read.
      const RECHECK_MS = 15 * 60 * 1000;
      const recent = (c) => c.endedAt && Date.now() - Date.parse(c.endedAt) < RECHECK_MS;
      const live = fresh.calls.filter((c) => c.status === 'placed' || c.status === 'queued'
        || (c.status === 'no_answer' && recent(c)));
      const polled = await Promise.all(live.slice(0, 10).map((c) => freight.getCall(c.id).catch(() => null)));
      const byId = new Map(polled.filter(Boolean).map((c) => [String(c.id), c]));
      setCalls(callsFromApi(fresh.calls.map((c) => byId.get(String(c.id)) || c), loads));
      setCallsConfigured(fresh.configured);
    } catch {
      /* a failed refresh leaves the last known history on screen rather than emptying it */
    } finally {
      setRefreshing(false);
    }
  }
  // "s1" was a seed id. With real dispatchers there is no such row, so the seat starts
  // unset and is filled from the live list once it arrives.
  const [seat, setSeat] = useState("");
  const [selected, setSelected] = useState(null);
  // ?view=recruiting&applicant=ID is where the recruiter email's "Open in the dashboard" lands.
  // `window` is absent when the render test runs this on the server.
  const linked = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const [view, setView] = useState(linked?.get("view") === "recruiting" ? "recruiting" : "overview");
  const [sheet, setSheet] = useState(false);
  const [call, setCall] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const driverOf = (l) => drivers.find((d) => d.id === l.driverId);
  const selectedLoad = loads.find((l) => l.id === selected) || null;

  /**
   * ⛔ THIS USED TO BE A PUPPET SHOW. Three effects and a script: a 1,400ms timer flipped
   * "dialing" to "connected", another fed pre-written lines in one at a time so a transcript
   * appeared to stream, and on the last line `finishCall()` wrote the step done and stamped
   * the load with `captured: { location: "Victoria, TX", eta: "8:40 PM" }` — a truck
   * position, hardcoded, filed against whichever real load the button was pressed on.
   * Nothing dialled. A dispatcher could not tell it from a working call, which is the whole
   * problem: the screen that proves Alfred called is the screen that must not be able to lie.
   *
   * Now: POST the call, then poll the platform for what actually happened.
   */
  useEffect(() => {
    if (!call || call.phase !== "placed" || !call.callId) return;
    let alive = true;
    const id = setInterval(async () => {
      const fresh = await freight.getCall(call.callId).catch(() => null);
      if (!alive || !fresh) return;
      if (["completed", "no_answer", "failed"].includes(fresh.status)) {
        const mapped = callsFromApi([fresh], loads)[0];
        // The panel stays open on the result so the transcript can be read; the plan is
        // advanced here, from what the platform reported, not from a timer.
        setCall((c) => (c && c.callId === call.callId ? { ...c, phase: "ended", result: mapped } : c));
        applyCallOutcome(call.loadId, call.stepId, mapped, call.prevStatus);
        refreshCalls();
      } else if (fresh.endedAt) {
        // Hung up, transcript not written yet: the backend holds an answered call open for it.
        setCall((c) => (c && c.callId === call.callId && !c.hungUp ? { ...c, hungUp: true } : c));
      }
    }, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [call && call.phase, call && call.callId]);

  // Elapsed since the platform accepted the call. Stops once it has ended — including the
  // hang-up, before the transcript lands.
  useEffect(() => {
    if (!call || (call.phase !== "placed" && call.phase !== "placing")) return;
    const id = setInterval(() => setCall((c) => (c && !c.hungUp ? { ...c, elapsed: c.elapsed + 1 } : c)), 1000);
    return () => clearInterval(id);
  }, [call && call.phase]);

  /**
   * A plan step → the reason the voice platform understands.
   *
   * The platform's enum is deliberately short: it composes what Alfred says, and this app
   * never writes the prompt. A step it cannot express falls back to a check call rather
   * than inventing a reason the API would reject.
   */
  function reasonForStep(st, load) {
    if (st.id === "assign" || /^relay/.test(st.id)) return "LOAD_OFFER";
    const stop = st.stopSeq != null ? (load.stops || []).find((x) => x.seq === st.stopSeq) : null;
    if (/^loaded/.test(st.id)) return "PICKUP_CONFIRM";
    if (/^pod/.test(st.id) || st.id === "delivered") return "DELIVERY_CONFIRM";
    if (/^arrive/.test(st.id)) return stop && stop.type === "drop" ? "DELIVERY_CONFIRM" : "PICKUP_CONFIRM";
    if (st.id === "yard" || st.id === "etapickup") return "ETA_STATUS";
    return "CHECK_IN";
  }

  /**
   * Ask the platform to ring this load's driver about this step.
   *
   * Three things stop a call before it is placed, and each says so rather than doing
   * nothing: there is no driver on the load, the driver has not consented to automated
   * calls, or the step is a BROKER call — for which there is no endpoint at all. The
   * backend dials drivers only (`POST /carrier/drivers/:id/call`); a broker leg would need
   * a dial path that does not exist, and a button that silently no-ops is worse than one
   * that explains itself.
   */
  async function callStep(loadId, stepId) {
    const load = loads.find((l) => l.id === loadId);
    if (!load) return;
    const st = (load.plan || []).find((s) => s.id === stepId);
    if (!st) return;
    const drv = driverOf(load);

    if (st.party !== "driver") {
      setCall({ loadId, stepId, phase: "blocked", partyName: load.broker?.contact || "the broker", elapsed: 0,
        error: "Alfred does not place broker calls from this screen yet — only driver calls are wired to the platform." });
      return;
    }
    if (!drv) {
      setCall({ loadId, stepId, phase: "blocked", partyName: "nobody", elapsed: 0,
        error: "This load has no driver assigned, so there is no one to call." });
      return;
    }
    // ⛔ CONSENT, NOT `canRequest`. These were the same field, and they are not the same
    // thing: `canRequest` is ALSO a toggle on the driver profile labelled "Can give Alfred
    // tasks", about the driver handing Alfred errands. Flipping that on for a driver who
    // never consented re-enabled this button — the backend still refused with 403
    // `no_consent`, so nobody was called, but the screen promised a dial it is forbidden to
    // make. TCPA consent does not belong behind a feature switch.
    if (!drv.consent) {
      setCall({ loadId, stepId, phase: "blocked", partyName: drv.name, elapsed: 0,
        error: `${drv.name} has not confirmed they agreed to automated calls, so Alfred will not dial them.` });
      return;
    }

    // 'calling' is screen-only, but a load an earlier bundle SAVED that way still says it. Read
    // as "the status before this call" it would be written straight back, so it counts as unknown.
    const prevStatus = load.status === "calling" ? null : load.status;
    // ⛔ BEFORE ANY AWAIT: this click is the gesture that lets the page make sound, and an AI
    // persona's call is heard here, live (LiveListen.jsx). After the await it is too late.
    const persona = !!drv.persona;
    if (persona) primeAudio();
    setCall({ loadId, stepId, party: "driver", prevStatus, partyName: drv.name, phase: "placing", elapsed: 0, callId: null, error: null, result: null, persona });
    try {
      const res = await freight.callDriver(drv.id, { reason: reasonForStep(st, load), loadId });
      const placed = res?.call || null;
      setCall((c) => (c ? { ...c, phase: "placed", callId: placed ? placed.id : null } : c));
      setLoads((ls) => ls.map((l) => (l.id === loadId ? { ...l, status: "calling" } : l)));
      refreshCalls();
    } catch (err) {
      // ⛔ MAY HAVE GONE (backend calls.mjs): the platform did not say whether it dialled. The call
      // is watched like a placed one, and the panel says what is known, never "Could not call".
      if (err?.body?.error === "platform_timeout" && err.body.call?.id) {
        setCall((c) => (c ? { ...c, phase: "placed", callId: err.body.call.id, doubt: true } : c));
        refreshCalls();
        return;
      }
      // The backend forwards the platform's own words — "driver_quiet_hours",
      // "from_number_not_found", "not_configured" — which are each actionable in a way
      // that "could not place call" is not.
      const reason = err?.body?.error || err?.error || null;
      const note = err?.body?.note || err?.body?.message || err?.message || null;
      setCall((c) => (c ? { ...c, phase: "failed", error: reason ? `${String(reason).replace(/_/g, " ")}${note ? ` — ${note}` : ""}` : (note || "The call could not be placed.") } : c));
      refreshCalls();
    }
  }

  /**
   * Record what the call actually did.
   *
   * Only a COMPLETED call advances the plan. A no-answer or a refusal puts the load back the
   * way it was — a call that did not happen is not progress, and the old code marked the step
   * done on a timer regardless of outcome. What is also gone is the invented part: no
   * fabricated truck position, and no "Driver confirmed" line for a call that went to
   * voicemail.
   *
   * Progress IS persisted: this writes through `updatePlan`, which calls `persistPlan`. Two
   * copies of this comment used to say "still local state — nothing persists plan progress
   * yet", which stopped being true in the same change that added the write.
   */
  function applyCallOutcome(loadId, stepId, result, prevStatus) {
    const completed = result && result.status === 'completed';
    setLoads((ls) => ls.map((l) => {
      if (l.id !== loadId) return l;
      if (!completed) return { ...l, status: prevStatus || l.status };
      const plan = (l.plan || []).map((s) => (s.id === stepId ? { ...s, state: 'done', at: nowLabel() } : s));
      const idx = plan.findIndex((s) => s.id === stepId);
      const nextIdx = plan.findIndex((s, k) => k > idx && s.state === 'scheduled');
      if (nextIdx > -1) plan[nextIdx] = { ...plan[nextIdx], state: 'due' };
      const step = (l.plan || []).find((s) => s.id === stepId);
      // ⛔ DO NOT FORCE 'rolling'. Completing a `delivered` or `pod` step used to set the
      // load back to Rolling, overwriting the real status in the view. Only the two steps
      // that genuinely describe a new state change it.
      // Every other step keeps the status the load had before the phone rang.
      const status = step && step.id === 'empty' ? 'empty'
        : (l.status === 'calling' ? (prevStatus || 'rolling') : l.status);
      // ⛔ THE STATUS GOES WITH THE PLAN. persistPlan read the load from a closure that still
      // said 'calling' — the screen-only state set while the phone rang — and saved THAT, so
      // after a reload the load sat "On the line" for good.
      persistPlan(loadId, plan, status);
      return {
        ...l,
        status,
        plan,
        activity: [...(l.activity || []), {
          t: nowLabel(),
          head: step ? step.title : 'Call',
          body: `Call completed${result.dur != null ? ` (${durLabel(result.dur)})` : ''}. The transcript is on this call in History.`,
        }],
      };
    }));
  }

  /**
   * Close the panel.
   *
   * ⛔ IT USED TO ALWAYS RESTORE `prevStatus`, which was right while the call was a
   * simulation the dispatcher could abandon. Against a real call it would roll a completed
   * load back to "rolling" the moment somebody closed the panel they had just read the
   * transcript in. The outcome has already been applied by then; closing only clears.
   */
  function endCall() {
    setCall((c) => {
      // Only a call that never reached the platform leaves the load mid-flight.
      if (c && (c.phase === "placing" || c.phase === "placed")) {
        setLoads((ls) => ls.map((l) => (l.id === c.loadId ? { ...l, status: c.prevStatus || l.status } : l)));
      }
      return null;
    });
  }

  /**
   * ⛔ CALL-PLAN PROGRESS IS NOW PERSISTED. It used to live in React state only, so a
   * dispatcher could work a load all morning — steps ticked off, times recorded, notes
   * typed — and lose every bit of it on a refresh, with the screen giving no hint that
   * anything was unsaved. The board even rendered "Completed at 3:42 PM" like a record.
   *
   * Only the DIFFERENCES are written (see planOverrides). The plan itself stays derived
   * from the load's stops, so adding a stop later still produces calls against it.
   *
   * ⛔ AND A FAILED SAVE IS VISIBLE. Fire-and-forget would recreate the original bug with
   * extra steps: the work would look saved and quietly not be. `loadError` already renders
   * at the top of the board, so a failure says so there.
   */
  async function persistPlan(loadId, plan, status) {
    const load = loads.find((l) => l.id === loadId);
    if (!load) return;
    // 'calling' exists only on this screen, while a phone rings. It is never the load's status
    // on the server, so it is never saved: the caller's status, else the one from before the call.
    const saveStatus = status
      ?? (load.status === 'calling' && call && call.loadId === loadId ? (call.prevStatus || load.status) : load.status);
    try {
      // ⛔ THE WHOLE LOAD, NOT A PATCH — AND THAT IS DELIBERATE.
      //
      // POST /carrier/loads is an upsert on (carrier_id, ref). The fixed backend updates
      // only the columns a caller sends, so `{ ref, detail }` would be correct against it.
      // But the frontend auto-deploys from main and the backend does not, so for some window
      // this bundle runs against a server whose ON CONFLICT still assigns EVERY column from
      // EXCLUDED — and against that server a two-key save empties the row: status back to
      // 'planned', the driver unassigned, and the rate, broker, paperwork and the entire stop
      // list gone. The call plan is derived from the stops, so saving the plan would delete
      // the plan.
      //
      // Sending the full load is correct against both: the old server overwrites each column
      // with the value it already held, and the new one updates every column it was handed
      // and merges the detail. A screen should not need to know which server it is talking
      // to in order to avoid destroying a row.
      await freight.saveLoad({
        ref: load.ref,
        status: saveStatus,
        rate: load.rate,
        driverId: load.driverId,
        broker: load.broker,
        stops: load.stops,
        paperwork: load.paperwork,
        detail: { ...(load.detail || {}), planOver: planOverrides(plan) },
      });
      setLoadError(null);
    } catch (err) {
      setLoadError(`The call plan for ${load.ref} did not save: ${err?.body?.message || err?.message || 'unknown error'}. It is still on screen, but a refresh will lose it.`);
    }
  }

  /** Apply a plan change to state AND write it back. One path, so nothing can skip the save. */
  function updatePlan(loadId, fn) {
    let next = null;
    setLoads((ls) => ls.map((l) => {
      if (l.id !== loadId) return l;
      next = fn(l);
      return next;
    }));
    if (next) persistPlan(loadId, next.plan);
  }

  const patchStep = (loadId, stepId, patch) =>
    updatePlan(loadId, (l) => ({ ...l, plan: l.plan.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) }));

  const addStep = (loadId, draft) =>
    updatePlan(loadId, (l) => ({ ...l, plan: [...l.plan, { id: "x" + Math.random().toString(36).slice(2, 6), party: "driver", extra: true, title: draft.title, detail: "Added by the office.", at: draft.at, state: "due", notes: "", captures: [] }] }));

  const setBrokerPref = (loadId, via) =>
    setLoads((ls) => ls.map((l) => (l.id === loadId ? { ...l, broker: { ...l.broker, updateVia: via } } : l)));

  function relay(loadId, toDriverId) {
    const to = drivers.find((d) => d.id === toDriverId);
    setLoads((ls) => ls.map((l) => {
      if (l.id !== loadId) return l;
      const from = drivers.find((d) => d.id === l.driverId);
      const plan = l.plan.map((s) => (s.state === "done" ? s : { ...s, state: s.state === "due" ? "scheduled" : s.state }));
      const assignIdx = plan.findIndex((s) => s.state !== "done");
      const relayStep = { id: "relay" + Math.random().toString(36).slice(2, 5), party: "driver", extra: true, title: `Relay assignment — ${to.name}`, detail: `Brief ${to.name} on the load from where ${from.name} left it, confirm equipment and the next appointment.`, at: nowLabel(), state: "due", notes: "", captures: [] };
      const next = [...plan.slice(0, assignIdx === -1 ? plan.length : assignIdx), relayStep, ...plan.slice(assignIdx === -1 ? plan.length : assignIdx)];
      return {
        ...l, driverId: toDriverId, plan: next,
        activity: [...l.activity, { t: nowLabel(), head: "Relay", body: `Remaining calls moved from ${from.name} to ${to.name} (${to.truck}).` }],
      };
    }));
  }

  function openLoadByRef(ref) {
    const l = loads.find((x) => x.ref === ref);
    if (l) { setView("loads"); setSelected(l.id); }
  }

  /**
   * Save a new load.
   *
   * ⛔ IT NEVER SAVED. It built the row, pushed it into React state, switched to the Loads
   * tab and selected it — so it looked completely successful and was gone on refresh.
   * `POST /api/carrier/loads` existed the whole time and was not called.
   *
   * It also invented `broker.cadence: "Every 4 hours"` for a broker nobody had asked, which
   * the Inspector then read back as "Alfred calls Dana at each milestone (every 4 hours)".
   */
  async function createLoad(f, source) {
    const stops = [
      { seq: 1, type: "pickup", name: f.puName || "Shipper", city: f.puCity, window: f.puWindow || "—", state: "pending" },
      { seq: 2, type: "drop", name: f.doName || "Consignee", city: f.doCity, window: f.doWindow || "—", state: "pending" },
    ];
    const payload = {
      ref: f.ref, status: "needs_driver", rate: f.rate, driverId: f.driverId || null, stops,
      broker: {
        name: f.brokerName || null, contact: f.brokerContact || null,
        phone: f.brokerPhone || null, updateVia: f.updateVia || null,
      },
      paperwork: [{ k: "BOL number", v: null }, { k: "Seal number", v: null }, { k: "Weight", v: null }, { k: "POD signer", v: null }],
      detail: { source, contact1: f.contact1 || null, contact2: f.contact2 || null },
    };
    setSheet(false);
    try {
      const saved = await freight.saveLoad(payload);
      const mapped = loadFromApi(saved);
      setLoads((ls) => [mapped, ...ls.filter((l) => l.id !== mapped.id)]);
      setView("loads"); setSelected(mapped.id);
      setLoadError(null);
    } catch (err) {
      // Do NOT leave a load on the board that the database rejected — that is the bug this
      // replaced, just with a worse ending.
      setLoadError(`The load ${f.ref || ""} could not be saved: ${err?.body?.message || err?.message || "unknown error"}`);
      setView("loads");
    }
  }

  const visible = loads.filter((l) => {
    const closed = CLOSED.includes(l.status);
    if (statusFilter === "active" && closed) return false;
    if (statusFilter === "closed" && !closed) return false;
    if (!q.trim()) return true;
    // ⛔ `driverOf(l).name` on an unassigned load — typing one character killed the tab.
    const dv = driverOf(l);
    const hay = [l.ref, dv && dv.name, dv && dv.truck, l.broker && l.broker.name,
      ...((l.stops || []).map((st) => `${st.name || ""} ${st.city || ""}`))].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const me = staffById(seat);

  return (
    <div className="alf-root" style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        .alf-bar { animation: alfBar 1s ease-in-out infinite; transform-origin: bottom; }
        @keyframes alfBar { 0%,100% { transform: scaleY(.28);} 50% { transform: scaleY(1);} }
        .alf-dot { width:5px; height:5px; border-radius:999px; background:#fff; animation: alfPulse 1.4s ease-in-out infinite; }
        @keyframes alfPulse { 0%,100% { opacity:.3;} 50% { opacity:1;} }
        .alf-fade { animation: alfFade .26s cubic-bezier(.4,0,.2,1); }
        @keyframes alfFade { from { opacity:0; transform: translateY(4px);} to { opacity:1; transform:none;} }
        .alf-rise { animation: alfRise .28s cubic-bezier(.32,.72,0,1); }
        @keyframes alfRise { from { opacity:0; transform: translateY(12px) scale(.99);} to { opacity:1; transform:none;} }
        .alf-row:hover { background:#FAFAFA; }
        .alf-btn { transition: opacity .15s, transform .1s; }
        .alf-btn:active { transform: scale(.975); }
        .alf-close:hover { color:#000; }
        .alf-root input::placeholder, .alf-root textarea::placeholder { color:${T.ink4}; }
        .alf-root input:focus, .alf-root select:focus, .alf-root textarea:focus { border-color:#000 !important; }
        .alf-root button:focus-visible, .alf-root input:focus-visible, .alf-root select:focus-visible, .alf-root textarea:focus-visible { outline:2px solid #000; outline-offset:2px; }
        @media (prefers-reduced-motion: reduce) { .alf-bar,.alf-dot,.alf-fade,.alf-rise { animation:none !important; } }
      `}</style>



      <header style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(255,255,255,0.9)", backdropFilter: "saturate(180%) blur(20px)", borderBottom: `1px solid ${T.hair}` }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 22px", minHeight: 54, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</span>
          <Segmented value={view} onChange={setView} options={[
            { v: "overview", label: "Overview" }, { v: "loads", label: "Loads" },
            { v: "drivers", label: "Drivers" }, { v: "recruiting", label: "Recruiting" }, { v: "history", label: "History" }, { v: "settings", label: "Settings" }]} />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {call && <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5 }}><Waveform active /> 1 call live</span>}
            {queue.length > 0 && view !== "overview" && (
              <button onClick={() => setView("overview")} style={{ font: "inherit", fontSize: 12.5, background: T.ink, color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer" }}>
                {queue.length} waiting on a person
              </button>
            )}
            <select value={seat} onChange={(e) => setSeat(e.target.value)} style={selectStyle} title="Who's at the keyboard">
              {/* ⛔ WAS `SEATS` — s1/s3/s4 with Renee, Priya and Hollis baked in, while
                  `seat` was initialised from the real dispatcher list. The dropdown showed
                  invented names for a real seat, and picking one set seat="s1", which
                  resolved to nobody, which made every "Mark handled" say "Only ops can
                  clear this one." */}
              {staff.length === 0
                ? <option value="">No dispatchers on file</option>
                : staff.map((p) => <option key={p.id} value={p.id}>{p.name}{p.role ? ` — ${p.role}` : ""}</option>)}
            </select>
            <Button onClick={() => setSheet(true)}>New load</Button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 22px 60px" }}>
        {/* ⛔ `loadError` WAS SET AND NEVER RENDERED. The `finally` marks the board loaded
            even when the fetch threw, so an API outage produced the honest-empty-state copy
            — "this carrier has no drivers yet" — for a carrier with a full roster. */}
        {loadError && (
          <div style={{ margin: "0 0 18px", border: `1px solid ${T.hair}`, borderLeft: `3px solid ${T.ink}`, borderRadius: 4, padding: "12px 14px", fontSize: 13, color: T.ink2 }}>
            <b style={{ color: T.ink }}>The board could not load.</b> {loadError} — so anything that looks empty below is unknown, not zero.
          </div>
        )}
        {STAGE_NOTICE[stage] && (
          <div role="status" style={{ margin: "0 0 18px", border: `1px solid ${T.hair}`, borderLeft: `3px solid ${T.ink}`, borderRadius: 4, padding: "12px 14px", fontSize: 13, color: T.ink2 }}>
            <b style={{ color: T.ink }}>{STAGE_NOTICE[stage][0]}</b> {STAGE_NOTICE[stage][1]}
          </div>
        )}
        {view === "overview" && <Overview queue={queue} drivers={drivers} loads={loads} calls={calls} seat={seat} onOpenLoad={openLoadByRef} onResolve={(id) => setResolved((r) => new Set(r).add(id))} />}
        {view === "history" && <History drivers={drivers} loads={loads} calls={calls} loaded={loaded} configured={callsConfigured} onRefresh={refreshCalls} refreshing={refreshing} />}
        {view === "settings" && <Settings />}
        {view === "recruiting" && (
          <Recruiting ui={{ T, Panel, PanelHead, Button, Toggle, Micro, Tag, Soft, Field, Segmented, mono }}
            initialApplicantId={linked?.get("applicant") || null} />
        )}
        {view === "drivers" && <div style={{ maxWidth: 780 }}><Drivers drivers={drivers} loads={loads} onOpenLoad={openLoadByRef} calls={calls} /></div>}

        {view === "loads" && (
          <div style={{ display: "flex", gap: 22, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 400px", minWidth: 300, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", gap: 8, padding: "12px 14px", borderBottom: `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search load, driver, truck, city, broker"
                  style={{ flex: 1, minWidth: 170, font: "inherit", fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "7px 10px", outline: "none" }} />
                <Segmented value={statusFilter} onChange={setStatusFilter} options={[{ v: "active", label: "Active" }, { v: "closed", label: "Closed" }, { v: "all", label: "All" }]} />
              </div>
              {visible.length === 0 && <div style={{ padding: "24px 20px", fontSize: 13.5, color: T.ink3 }}>No loads match.</div>}
              {visible.map((l, i) => {
                const d = driverOf(l);
                const isSel = l.id === selected;
                const isLive = call && call.loadId === l.id;
                const flags = queue.filter((x) => x.loadRef === l.ref).length;
                const due = l.plan.find((s) => s.state === "due");
                const det = openDetention(l);
                const late = lateRisk(l);
                return (
                  <button key={l.id} onClick={() => setSelected(l.id)} className="alf-row" style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", border: "none",
                    borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, background: isSel ? "#F0F0F0" : "transparent",
                    cursor: "pointer", textAlign: "left", font: "inherit", position: "relative" }}>
                    <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: isLive || flags ? T.ink : "transparent" }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3, letterSpacing: 0.3 }}>{l.ref}</span>
                        <Chip status={isLive ? "calling" : l.status} />
                        {late && <Tag>late risk</Tag>}
                        {det && <Tag solid>detention</Tag>}
                        {flags > 0 && !isLive && <span style={{ fontSize: 11.5, borderBottom: `1px solid ${T.ink}` }}>{flags} waiting</span>}
                      </span>
                      <span style={{ display: "block", fontSize: 15, fontWeight: 500, letterSpacing: -0.3, marginTop: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{laneLabel(l)}</span>
                      <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {/* ⛔ `d` IS UNDEFINED ON AN UNASSIGNED LOAD. loads.driver_id is nullable
                            and the demo seed writes one deliberately; a deactivated driver also
                            leaves a load pointing at a row listDrivers no longer returns. The
                            search filter two lines down was already guarded for this and the
                            renderer was not. */}
                        {d ? d.name : "No driver assigned"} · {due ? `next: ${due.title}${due.at ? ` at ${due.at}` : ""}` : "plan complete"}
                      </span>
                    </span>
                    <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
                  </button>
                );
              })}
            </div>

            <div style={{ flex: "1 1 400px", minWidth: 300, position: "sticky", top: 80, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, minHeight: 340, maxHeight: "calc(100vh - 110px)", overflow: "auto" }}>
              <Inspector load={selectedLoad} driver={selectedLoad ? driverOf(selectedLoad) : null} drivers={drivers} calls={calls} call={call}
                onCallStep={callStep} onEndCall={endCall} onPatchStep={patchStep} onAddStep={addStep}
                onSetBrokerPref={setBrokerPref} onRelay={relay} onClose={() => setSelected(null)} />
            </div>
          </div>
        )}
      </main>

      {sheet && <NewLoadSheet onClose={() => setSheet(false)} onCreate={createLoad} drivers={drivers} />}
    </div>
  );
}
